import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Search,
  PenLine,
  Loader2,
  Check,
  Layers,
  ArrowLeft,
  Eye,
  Radio,
  ChevronDown,
} from "lucide-react";
import ReactSelect, { SingleValue, StylesConfig } from "react-select";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import PageHeader from "../../../components/PageHeader";
import Modal from "../../../components/Modal";
import { COLORS } from "../../../constants";
import {
  controleProducaoService,
  FichaDiariaItem,
  FichaDiariaResponse,
  CronometragemItem,
  PausaFichaItem,
} from "../../../services/controleProducaoService";
import { useAuth } from "../../../context/AuthContext";
import { lojaService } from "../../../services/lojaService";
import { Loja } from "../../../types/loja";
import { BrazilDate } from "../../../utils/BrazilDate";
import {
  addDaysISO,
  getDataFichaOperacional,
  getTurnoProducao,
  labelTurnoCurto,
  formatHorarioTurnoAtual,
  labelTurnoComHorarios,
  isAmbienteLocalFrontend,
  lerTurnoSimuladoStorage,
  gravarTurnoSimuladoStorage,
} from "../../../utils/controleProducaoTurno";
import {
  labelDiaSemanaFromISO,
  mondayOfISO,
} from "../../../utils/controleProducaoDates";
import {
  computeSugestaoProducao,
  resolveUndFicha,
  sugestaoProducaoIsentaCronometro,
  itemNaoUsaTimerNaFicha,
  undNomeById,
} from "../../../utils/controleProducaoQuantidade";
import {
  canSelecionarLojaFichaDiaria,
  canAccessPath,
  hasFullAccessUser,
  isDesenvolvedorRoleNome,
  podeVerFichaCompletaUser,
  podeVerMaisInfoFichaProducaoUser,
} from "../../../lib/screenAccess";
import OrientacoesProducaoDisplay from "../../../components/ControleProducao/OrientacoesProducaoDisplay";
import FichaDiariaAssinaturas from "./FichaDiariaAssinaturas";
import FichaDiariaResumoGestao from "./FichaDiariaResumoGestao";
import FichaDiariaEquipeSetorModal, {
  BotaoEquipeSetor,
  type EquipeSetorAlvo,
} from "./FichaDiariaEquipeSetorModal";
import FichaDiariaVisaoCompletaModal from "./FichaDiariaVisaoCompletaModal";
import AssinaturaPenAnimacao from "./AssinaturaPenAnimacao";
import UndMedidaSelect from "../../../components/ControleProducao/UndMedidaSelect";
import type { UndMedidaOption } from "../../../services/controleProducaoService";
import FichaDiariaPrintSheet from "./FichaDiariaPrintSheet";
import {
  abrirSobrasPdf,
  compartilharSobrasPdfWhatsApp,
  type FichaDiariaSobrasPdfInput,
} from "./fichaDiariaSobrasPdf";
import { SignaturePadField } from "../../../components/ui/signature-pad";
import { useRankingEvents } from "../../../hooks/useRankingEvents";
import { agruparItensFichaPorSetor } from "./agruparItensFichaPorSetor";
import FichaDiariaContextPanel, {
  type FichaDiariaContextMessage,
} from "./FichaDiariaContextPanel";
import ProducaoToggle from "./ProducaoToggle";
import CronometroItemButtons, {
  cronometragemComTick,
  motivoBloqueioLancamentoProducao,
} from "./CronometroItemButtons";
import { pedirJustificativaPausa } from "./pedirJustificativaPausa";

/** DD-MM-AAAA para nome do PDF ao salvar impressão. */
function dataIsoParaNomeArquivo(dataIso: string): string {
  const [ano, mes, dia] = dataIso.split("-");
  if (!ano || !mes || !dia) return dataIso;
  return `${dia}-${mes}-${ano}`;
}

function tituloPdfFichaProducao(
  dataIso: string,
  nomeLoja: string,
  nomeSetor?: string | null,
  modoNoite?: boolean
): string {
  const data = dataIsoParaNomeArquivo(dataIso);
  const loja = nomeLoja.trim() || "Sem loja";
  if (modoNoite) {
    return `Ficha Diaria Noite - Sobras ${data} - Loja ${loja}`;
  }
  const setor = nomeSetor?.trim() ? ` - ${nomeSetor.trim()}` : "";
  return `Ficha Produção Dia ${data} - Loja ${loja}${setor}`;
}

function cronometragemDoGrupo(
  item: FichaDiariaItem | undefined,
  itens: FichaDiariaItem[] | undefined
): CronometragemItem | null | undefined {
  if (!item) return undefined;
  if (item.cronometragem) return item.cronometragem;
  if (!itens?.length) return item.cronometragem;
  const linhaLocal = itens.find(
    (i) => i.itemId === item.itemId && !i.lojaIdOutra
  );
  return linhaLocal?.cronometragem ?? item.cronometragem;
}

function bloqueioLancamentoProducaoLinha(opts: {
  turnoDia: boolean;
  foraDaUnidade: boolean;
  jaProduziu: boolean;
  cronometragem: CronometragemItem | null | undefined;
  nowMs: number;
  isentaPorSugestaoZero?: boolean;
}): string | null {
  if (!opts.turnoDia || opts.foraDaUnidade || opts.isentaPorSugestaoZero) {
    return null;
  }
  const ticked = cronometragemComTick(opts.cronometragem, opts.nowMs);
  return motivoBloqueioLancamentoProducao({
    status: ticked?.status,
    tempoExibidoMs: ticked?.tempoLiquidoMs ?? 0,
    jaProduziu: opts.jaProduziu,
  });
}

function grupoTemOutraLojaPendente(
  itemIdLocal: number,
  linhas: Record<number, LinhaLocal>,
  itens: Array<{ itemId: number; lojaIdOutra?: number; itemIdOutraLoja?: number }>
): boolean {
  return itens.some((item) => {
    if (item.itemId !== itemIdLocal || !item.lojaIdOutra) return false;
    const local = linhas[item.itemIdOutraLoja!];
    return (
      local?.producaoChecada !== true &&
      local?.naoProduzidoPelaUnidade !== true
    );
  });
}

function cronometragemEncerradaSeProduziu(
  cronometragem: CronometragemItem | null | undefined,
  nowMs: number
): CronometragemItem | null | undefined {
  if (!cronometragem) return cronometragem;
  if (
    cronometragem.status !== "RUNNING" &&
    cronometragem.status !== "PAUSED"
  ) {
    return cronometragem;
  }
  const ticked =
    cronometragemComTick(cronometragem, nowMs) ?? cronometragem;
  return {
    ...ticked,
    status: "FINISHED",
    runningSince: null,
    finishedAt: ticked.finishedAt ?? new Date(nowMs).toISOString(),
  };
}

type LinhaLocal = {
  itemId: number;
  baseValor: string;
  undMedidaIdBase: number | "";
  sobra: string;
  undMedidaIdSobra: number | "";
  producao: string;
  undMedidaIdProducao: number | "";
  producaoChecada: boolean;
  naoProduzidoPelaUnidade: boolean;
  justificativaNaoProduziu: string;
  observacaoSobra: string;
  observacaoProducao: string;
};

type ModalObservacaoTipo = "sobra" | "producao";

function normalizarBuscaItem(texto: string): string {
  return texto
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ")
    .normalize("NFD")
    .replace(/\p{M}/gu, "");
}

function undFichaDoItem(
  item: Pick<FichaDiariaItem, "undMedidaIdBase">,
  salva?: number | "" | null
): number | "" {
  return resolveUndFicha(item.undMedidaIdBase, salva);
}

function sobraPersistidaLinha(
  item: FichaDiariaItem,
  local?: LinhaLocal
): string {
  return (local?.sobra ?? item.sobra ?? "").trim();
}

function quantidadeDeExibicao(texto: string | undefined | null): string {
  const t = (texto ?? "").trim();
  if (!t) return "";
  const match = t.match(/^([\d.,]+)/);
  return match ? match[1] : t;
}

function sobraReferenciaQuantidadeLinha(item: FichaDiariaItem): string {
  return (
    quantidadeDeExibicao(item.sobraExibicao) ||
    item.sobraReferencia?.trim() ||
    ""
  );
}

/** Valor do campo Sobra: só o persistido / digitado. Referência do dia anterior não preenche. */
function sobraExibicaoLinha(
  item: FichaDiariaItem,
  local: LinhaLocal | undefined,
  _podeEditarSobra: boolean
): string {
  if (local) return (local.sobra ?? "").trim();
  return (item.sobra ?? "").trim();
}

function undSobraExibicaoLinha(
  item: FichaDiariaItem,
  local: LinhaLocal | undefined,
  _podeEditarSobra: boolean
): number | "" {
  if (local) {
    return undFichaDoItem(item, local.undMedidaIdSobra);
  }
  if ((item.sobra ?? "").trim()) {
    return undFichaDoItem(item, item.undMedidaIdSobra);
  }
  if (sobraReferenciaQuantidadeLinha(item)) {
    return undFichaDoItem(
      item,
      item.undMedidaIdSobraReferencia ?? item.undMedidaIdSobra
    );
  }
  return undFichaDoItem(item, item.undMedidaIdSobra);
}

function sugestaoProducaoLinha(
  item: FichaDiariaItem,
  local: LinhaLocal | undefined,
  unds: UndMedidaOption[]
): string {
  const persistida = sobraPersistidaLinha(item, local);
  if (
    !persistida &&
    !(local?.producao ?? "").trim() &&
    item.sugestaoProducao
  ) {
    return item.sugestaoProducao;
  }
  const baseValor = local?.baseValor ?? item.baseValor ?? "";
  const undBase = undNomeById(
    undFichaDoItem(item, local?.undMedidaIdBase),
    unds
  );
  const sobraTexto =
    persistida || sobraReferenciaQuantidadeLinha(item);
  const undSobra = undNomeById(
    undSobraExibicaoLinha(item, local, false),
    unds
  );
  const calculada = computeSugestaoProducao(
    baseValor,
    undBase || null,
    sobraTexto,
    undSobra || null
  );
  if (!calculada && item.sugestaoProducao) {
    return item.sugestaoProducao;
  }
  return calculada;
}

function linhaIsentaCronometroPorSugestaoZero(
  item: FichaDiariaItem,
  local: LinhaLocal | undefined,
  unds: UndMedidaOption[],
  dataFichaISO?: string | null
): boolean {
  if (itemNaoUsaTimerNaFicha(item.naoUsaTimer, dataFichaISO)) return true;
  return sugestaoProducaoIsentaCronometro(
    sugestaoProducaoLinha(item, local, unds)
  );
}

function grupoIsentoCronometroPorSugestaoZero(
  itensGrupo: FichaDiariaItem[],
  linhas: Record<number, LinhaLocal>,
  unds: UndMedidaOption[],
  overlaySetor: boolean,
  dataFichaISO?: string | null
): boolean {
  if (itensGrupo.length === 0) return false;
  return itensGrupo.every((gi) => {
    const gk = gi.lojaIdOutra ? gi.itemIdOutraLoja! : gi.itemId;
    const gl = linhaVisualSetor(gi, linhas[gk], overlaySetor);
    return linhaIsentaCronometroPorSugestaoZero(gi, gl, unds, dataFichaISO);
  });
}

const JUSTIFICATIVA_SEM_SOBRA_PADRAO = "Não houve sobra neste dia.";
const PREFIXO_RASCUNHO_FICHA = "ficha-diaria-draft:";

function limparRascunhosFichaDiaria(): void {
  try {
    const chaves: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const chave = localStorage.key(i);
      if (chave?.startsWith(PREFIXO_RASCUNHO_FICHA)) chaves.push(chave);
    }
    chaves.forEach((chave) => localStorage.removeItem(chave));
  } catch {
    // ignore
  }
}

function linhaTemSobra(sobra: string | null | undefined): boolean {
  return (sobra ?? "").trim() !== "";
}

function linhaProducaoResolvida(local: LinhaLocal | undefined): boolean {
  if (!local) return false;
  if (local.naoProduzidoPelaUnidade) return true;
  const producao = (local.producao ?? "").trim();
  const observacaoProducao = (local.observacaoProducao ?? "").trim();
  if (local.producaoChecada) return producao !== "";
  return observacaoProducao !== "";
}

function itemProducaoOk(
  local: LinhaLocal | undefined,
  producaoAssinada: boolean
): boolean {
  if (producaoAssinada) return true;
  return linhaProducaoResolvida(local);
}

function classProducaoInput(
  editavel: boolean,
  local: LinhaLocal | undefined,
  producaoAssinada: boolean,
  layout: "mobile" | "table" = "mobile"
): string {
  const ok = itemProducaoOk(local, producaoAssinada);
  const base =
    layout === "table"
      ? inputQtyTabelaClass
      : "w-full min-h-11 min-w-0 rounded-lg border px-3 py-2 text-base";
  if (ok) {
    if (!producaoAssinada && local?.naoProduzidoPelaUnidade) {
      return `${base} border-slate-400 bg-slate-100 text-slate-900${
        !editavel ? " cursor-not-allowed" : ""
      }`;
    }
    const naoProduzido =
      !producaoAssinada && local != null && !local.producaoChecada;
    if (naoProduzido) {
      return `${base} border-amber-400 bg-amber-50 text-amber-950${
        !editavel ? " cursor-not-allowed" : ""
      }`;
    }
    return `${base} border-emerald-500 bg-emerald-100 text-black${
      !editavel ? " cursor-not-allowed" : ""
    }`;
  }
  if (!editavel) {
    return `${base} cursor-not-allowed border-neutral-300 bg-neutral-100 text-neutral-800`;
  }
  return `${base} border-red-400 bg-red-50 text-red-700`;
}

const hover3dCampoClass =
  "transition-all duration-200 ease-out hover:-translate-y-0.5 hover:drop-shadow-[0_10px_14px_rgba(15,23,42,0.18)]";

const inputDesabilitadoClass =
  "w-full min-w-0 cursor-not-allowed rounded-md border border-neutral-300 bg-neutral-100 px-1.5 py-1.5 text-sm text-neutral-800 shadow-[inset_0_1px_2px_rgba(15,23,42,0.06)]";

const inputBaseTabelaClass =
  "min-w-0 w-full rounded-md border border-neutral-300 px-1.5 py-1.5 text-sm shadow-[inset_0_1px_2px_rgba(15,23,42,0.06)]";
const inputQtyTabelaClass =
  "min-w-0 w-full rounded-md border border-neutral-300 px-1.5 py-1.5 text-sm shadow-[inset_0_1px_2px_rgba(15,23,42,0.06)]";
const undSelectTabelaClass = "w-[5.5rem] min-w-[5.5rem] shrink-0";
const celulaTabelaClass =
  "border-r border-neutral-200 px-2 py-2 align-middle last:border-r-0";
const cabecalhoTabelaClass =
  "border-r border-neutral-200 px-2 py-2.5 text-left font-semibold text-neutral-700 last:border-r-0";

type FichaSelectOpt = { value: number; label: string };

const fichaSelectStyles: StylesConfig<FichaSelectOpt, false> = {
  control: (base) => ({
    ...base,
    minHeight: 40,
    marginTop: 4,
    fontSize: "0.875rem",
    borderColor: COLORS.NEUTRAL_300,
    boxShadow: "none",
    "&:hover": { borderColor: COLORS.PRIMARY },
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? COLORS.PRIMARY
      : state.isFocused
        ? COLORS.NEUTRAL_100
        : COLORS.WHITE,
    color: state.isSelected ? COLORS.WHITE : COLORS.NEUTRAL_900,
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  indicatorSeparator: () => ({ display: "none" }),
};

type CampoQtyTabelaProps = {
  value: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  unds: UndMedidaOption[];
  undId: number | "";
  onUndChange?: (id: number | "") => void;
  podeAlterarUnd: boolean;
  className: string;
  placeholder?: string;
  tabIndex?: number;
};

function CampoQtyTabela({
  value,
  onChange,
  disabled = false,
  unds,
  undId,
  onUndChange,
  podeAlterarUnd,
  className,
  placeholder,
  tabIndex,
}: CampoQtyTabelaProps) {
  const nome = undNomeById(undId, unds) || "—";
  const mostrarSelect = Boolean(podeAlterarUnd && !disabled && onUndChange);
  return (
    <div
      className={`flex w-full min-w-0 items-stretch gap-1 rounded-md ${
        disabled ? "" : hover3dCampoClass
      }`}
    >
      <input
        type="text"
        disabled={disabled}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange?.(e.target.value)}
        className={`min-w-0 flex-1 ${className}`}
        inputMode="decimal"
        tabIndex={tabIndex}
      />
      {mostrarSelect ? (
        <UndMedidaSelect
          unds={unds}
          value={undId}
          onChange={onUndChange!}
          className={undSelectTabelaClass}
        />
      ) : (
        <span
          className="flex shrink-0 items-center px-0.5 text-xs font-medium text-neutral-600"
          title={nome}
        >
          {nome}
        </span>
      )}
    </div>
  );
}

const gridUndCardClass =
  "grid min-w-0 grid-cols-[minmax(0,1fr)_2.5rem] items-stretch gap-1";
const gridUndCompactClass =
  "grid min-w-0 grid-cols-[minmax(0,1fr)_1.75rem] items-stretch gap-0.5";
const undSelectCardClass = "h-7 min-w-0 w-full";
const undSelectCompactClass = "h-7 min-w-0 w-full";

function estadoLinhaProducao(
  local: LinhaLocal | undefined,
  producaoAssinada: boolean
): "assinada" | "produzido" | "nao_produzido" | "nao_produzido_unidade" | "pendente" {
  if (producaoAssinada) return "assinada";
  if (!local) return "pendente";
  if (local.naoProduzidoPelaUnidade) return "nao_produzido_unidade";
  if (local.producaoChecada && (local.producao ?? "").trim())
    return "produzido";
  if (!local.producaoChecada && (local.observacaoProducao ?? "").trim()) {
    return "nao_produzido";
  }
  return "pendente";
}

/** Em blocos por setor, a produção/status/cronômetro vêm do vínculo — não do consolidado da linha. */
function linhaVisualSetor(
  item: FichaDiariaItem,
  local: LinhaLocal | undefined,
  overlaySetor: boolean
): LinhaLocal | undefined {
  if (!overlaySetor) return local;
  const base = local ?? {
    itemId: item.lojaIdOutra ? item.itemIdOutraLoja! : item.itemId,
    baseValor: item.baseValor || "",
    undMedidaIdBase: undFichaDoItem(item, item.undMedidaIdBase),
    sobra: "",
    undMedidaIdSobra: undFichaDoItem(item, item.undMedidaIdSobra),
    producao: "",
    undMedidaIdProducao: undFichaDoItem(item, item.undMedidaIdProducao),
    producaoChecada: false,
    naoProduzidoPelaUnidade: false,
    justificativaNaoProduziu: "",
    observacaoSobra: "",
    observacaoProducao: "",
  };
  return {
    ...base,
    producao: item.producao || "",
    undMedidaIdProducao: undFichaDoItem(item, item.undMedidaIdProducao),
    producaoChecada: item.producaoChecada ?? false,
    naoProduzidoPelaUnidade: item.naoProduzidoPelaUnidade ?? false,
    justificativaNaoProduziu: item.justificativaNaoProduziu || "",
    observacaoProducao: item.observacaoProducao || "",
    sobra: (item.sobra ?? "").trim() || base.sobra,
  };
}

function baseBotaoObservacao(layout: "card" | "tabela"): string {
  if (layout === "tabela") {
    return "flex min-w-0 w-full items-center justify-center gap-1 rounded-md border px-1.5 py-1.5 text-[11px] font-medium leading-none";
  }
  return "min-h-11 rounded-lg border text-left text-sm leading-snug px-3 py-2.5 break-words touch-manipulation";
}

function classBotaoObservacaoProducao(
  local: LinhaLocal | undefined,
  podeEditarProducao: boolean,
  layout: "card" | "tabela" = "card"
): string {
  const base = baseBotaoObservacao(layout);
  const observacao = (local?.observacaoProducao ?? "").trim();
  if (observacao) {
    if (
      podeEditarProducao &&
      local?.producaoChecada !== true &&
      !local?.naoProduzidoPelaUnidade
    ) {
      return `${base} border-amber-300 bg-amber-50 text-amber-950`;
    }
    return `${base} border-sky-200 bg-sky-50 text-sky-900`;
  }
  if (local?.naoProduzidoPelaUnidade) {
    return `${base} border-slate-200 bg-slate-50 text-neutral-500`;
  }
  if (podeEditarProducao && local?.producaoChecada !== true) {
    return `${base} border-red-300 bg-red-50 text-red-800`;
  }
  return `${base} border-neutral-300 bg-white text-neutral-500`;
}

function classBotaoObservacaoSobra(
  local: LinhaLocal | undefined,
  podeEditarSobra: boolean,
  layout: "card" | "tabela" = "card"
): string {
  const base = baseBotaoObservacao(layout);
  const observacao = (local?.observacaoSobra ?? "").trim();
  if (observacao) {
    return `${base} border-sky-200 bg-sky-50 text-sky-900`;
  }
  if (podeEditarSobra) {
    return `${base} border-neutral-300 bg-white text-neutral-500`;
  }
  return `${base} border-neutral-200 bg-neutral-50 text-neutral-500`;
}

function IndicadorObsPreenchida() {
  return (
    <span
      className="inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full bg-sky-600 text-white"
      title="Observação preenchida"
      aria-label="Observação preenchida"
    >
      <Check className="h-2.5 w-2.5" strokeWidth={3} />
    </span>
  );
}

function pillUnidadeClass(isOutra: boolean): string {
  return `rounded-full px-1.5 py-px text-[9px] font-bold normal-case ${
    isOutra ? "bg-sky-100 text-sky-800" : "bg-emerald-100 text-emerald-800"
  }`;
}

function labelUnidadeFicha(item: FichaDiariaItem): string {
  return (item.unidadeLabel ?? "").replace("Dom Belloni - ", "");
}

function AccordionMobileFicha({
  titulo,
  preenchido,
  alerta,
  children,
}: {
  titulo: string;
  preenchido?: boolean;
  alerta?: boolean;
  children: React.ReactNode;
}) {
  return (
    <details
      className={`group rounded-lg border ${
        alerta
          ? "border-red-300 bg-red-50/70"
          : "border-neutral-200 bg-neutral-50"
      }`}
    >
      <summary className="flex min-h-7 cursor-pointer list-none items-center justify-between gap-2 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-neutral-600 [&::-webkit-details-marker]:hidden">
        <span className="flex items-center gap-1.5">
          {titulo}
          {preenchido ? <IndicadorObsPreenchida /> : null}
        </span>
        <ChevronDown className="h-4 w-4 shrink-0 text-neutral-400 transition-transform group-open:rotate-180" />
      </summary>
      <div className="space-y-1.5 border-t border-neutral-200/80 px-2 py-1.5">
        {children}
      </div>
    </details>
  );
}

const ControleProducaoFichaDiariaPage: React.FC<{
  modoDesenvolvedor?: boolean;
  lojaIdControlada?: number;
  dataFichaControlada?: string;
  ocultarSeletoresLojaData?: boolean;
  turnoSimuladoControlado?: "dia" | "noite";
  ocultarSeletorTurnoTeste?: boolean;
}> = ({
  modoDesenvolvedor = false,
  lojaIdControlada,
  dataFichaControlada,
  ocultarSeletoresLojaData = false,
  turnoSimuladoControlado,
  ocultarSeletorTurnoTeste = false,
}) => {
  const { user } = useAuth();
  const [dataFicha, setDataFicha] = useState(() => getDataFichaOperacional());
  const [buscaItem, setBuscaItem] = useState("");
  const [selectedLojaId, setSelectedLojaId] = useState<number | undefined>(
    user?.lojaId
  );
  const [lojas, setLojas] = useState<Loja[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingManual, setSavingManual] = useState(false);
  const [autoSaving, setAutoSaving] = useState(false);
  const [assinandoProducao, setAssinandoProducao] = useState(false);
  const [assinandoProducaoTotal, setAssinandoProducaoTotal] = useState(false);
  const [assinandoConferencia, setAssinandoConferencia] = useState(false);
  const [preparandoSobrasPdf, setPreparandoSobrasPdf] = useState(false);
  const [selectedSetorId, setSelectedSetorId] = useState<number | undefined>();
  const [turnoSimulado, setTurnoSimulado] = useState<"dia" | "noite">(
    () =>
      turnoSimuladoControlado ??
      lerTurnoSimuladoStorage() ??
      getTurnoProducao(),
  );
  const [gestaoNoGeral, setGestaoNoGeral] = useState(
    () => (lerTurnoSimuladoStorage() ?? getTurnoProducao()) !== "noite",
  );
  const [nowTick, setNowTick] = useState(() => Date.now());
  const [tecladoAberto, setTecladoAberto] = useState(false);
  const payloadLoadedAtRef = useRef(Date.now());
  const [modalAssinatura, setModalAssinatura] = useState<
    "producao" | "producao_total" | "conferencia" | null
  >(null);
  const [assinaturaDesenhada, setAssinaturaDesenhada] = useState(false);
  const [assinaturaImagem, setAssinaturaImagem] = useState<string | null>(null);
  const [modalObservacao, setModalObservacao] = useState<{
    itemId: number;
    descricao: string;
    tipo: ModalObservacaoTipo;
    justificativaNaoProduzido?: boolean;
  } | null>(null);
  const [observacaoDraft, setObservacaoDraft] = useState("");
  const [justificativaSemSobraDraft, setJustificativaSemSobraDraft] = useState(
    JUSTIFICATIVA_SEM_SOBRA_PADRAO
  );
  const [payload, setPayload] = useState<FichaDiariaResponse | null>(null);
  const [unds, setUnds] = useState<UndMedidaOption[]>([]);
  const [linhas, setLinhas] = useState<Record<number, LinhaLocal>>({});
  const skipNextAutosaveRef = useRef(false);
  const linhasAlteradasPeloUsuarioRef = useRef(false);
  const linhasRef = useRef<Record<number, LinhaLocal>>({});
  const saveChainRef = useRef<Promise<void>>(Promise.resolve());
  const saveInFlightRef = useRef(false);
  const dirtyItemIdsRef = useRef<Set<number>>(new Set());
  const linhasServidorRef = useRef<Record<number, LinhaLocal>>({});
  const [pausasModalOpen, setPausasModalOpen] = useState(false);
  const [pausas, setPausas] = useState<PausaFichaItem[]>([]);
  const [loadingPausas, setLoadingPausas] = useState(false);
  const [pausaFiltroUsuario, setPausaFiltroUsuario] = useState("");
  const [pausaFiltroItem, setPausaFiltroItem] = useState("");
  const [pausaFiltroLongas, setPausaFiltroLongas] = useState(false);
  const [pausaPagina, setPausaPagina] = useState(0);
  const [equipeSetor, setEquipeSetor] = useState<EquipeSetorAlvo | null>(null);
  const [modalFichaCompleta, setModalFichaCompleta] = useState(false);

  const dataFichaOperacional =
    payload?.dataFichaPadrao ?? getDataFichaOperacional();
  const isFichaOperacionalAtual = dataFicha === dataFichaOperacional;
  const fichaHistorica = !isFichaOperacionalAtual;

  const isAdmin = hasFullAccessUser(user);
  const podeVerPausas = isAdmin || user?.roleId === 8;
  const podeSelecionarLoja = canSelecionarLojaFichaDiaria(user);
  const podeSimularTurno =
    payload?.podeSimularTurno === true ||
    isAmbienteLocalFrontend() ||
    isDesenvolvedorRoleNome(user?.role?.nome);
  const turnoSimuladoEnvio =
    turnoSimuladoControlado ??
    (podeSimularTurno ? turnoSimulado : undefined);

  const lojaOptions = useMemo(
    () =>
      podeSelecionarLoja
        ? lojas
        : lojas.filter((loja) => loja.id === user?.lojaId),
    [podeSelecionarLoja, lojas, user?.lojaId]
  );

  const nomeLojaSelecionada = useMemo(
    () =>
      lojaOptions.find((l) => l.id === selectedLojaId)?.nome ||
      user?.loja?.nome ||
      "",
    [lojaOptions, selectedLojaId, user?.loja?.nome]
  );
  const podeAbrirFichaTransferencia = canAccessPath(
    user,
    "/controle-producao/ficha-transferencia"
  );

  const loadFicha = useCallback(
    async (
      dateValue: string,
      lojaId?: number,
      setorId?: number,
      opts?: { silent?: boolean }
    ) => {
      const silent = opts?.silent === true;
      if (silent && saveInFlightRef.current) {
        return;
      }
      if (!silent) {
        linhasAlteradasPeloUsuarioRef.current = false;
        setLoading(true);
      }
      try {
        const setorConsulta =
          (turnoSimuladoEnvio ?? getTurnoProducao()) === "noite"
            ? undefined
            : setorId;
        const data = await controleProducaoService.getFichaDiaria(
          dateValue,
          lojaId,
          setorConsulta,
          turnoSimuladoEnvio
        );
        setPayload(data);
        payloadLoadedAtRef.current = Date.now();
        setNowTick(Date.now());
        setUnds(data.unds ?? []);
        if (
          !silent &&
          data.setorAtual?.id &&
          data.setorAtual.id !== setorId
        ) {
          setSelectedSetorId(data.setorAtual.id);
        }
        const next: Record<number, LinhaLocal> = {};
        (data.itens || []).forEach((item) => {
          const key = item.lojaIdOutra ? item.itemIdOutraLoja! : item.itemId;
          const undBase = undFichaDoItem(item, item.undMedidaIdBase);
          const sobraInicial = (item.sobra || "").trim();
          const undSobra = undFichaDoItem(
            item,
            sobraInicial
              ? item.undMedidaIdSobra
              : (item.undMedidaIdSobraReferencia ?? item.undMedidaIdSobra)
          );
          const undProducao = undFichaDoItem(item, item.undMedidaIdProducao);
          next[key] = {
            itemId: key,
            baseValor: item.baseValor || "",
            undMedidaIdBase: undBase,
            sobra: sobraInicial,
            undMedidaIdSobra: undSobra,
            producao: item.producao || "",
            undMedidaIdProducao: undProducao,
            producaoChecada: item.producaoChecada ?? false,
            naoProduzidoPelaUnidade: item.naoProduzidoPelaUnidade ?? false,
            justificativaNaoProduziu: item.justificativaNaoProduziu || "",
            observacaoSobra: item.observacaoSobra || "",
            observacaoProducao: item.observacaoProducao || "",
          };
        });
        if (silent) {
          setLinhas((prev) => {
            const mesclado = { ...next };
            dirtyItemIdsRef.current.forEach((id) => {
              if (prev[id]) mesclado[id] = prev[id];
            });
            linhasRef.current = mesclado;
            return mesclado;
          });
          if (dirtyItemIdsRef.current.size === 0) {
            linhasServidorRef.current = next;
          }
          return;
        }
        linhasServidorRef.current = next;
        dirtyItemIdsRef.current = new Set();
        linhasAlteradasPeloUsuarioRef.current = false;
        setLinhas(next);
        linhasRef.current = next;
        limparRascunhosFichaDiaria();
      } catch (error: any) {
        toast.error(error?.message || "Erro ao carregar ficha diária.");
        if (!silent) {
          setPayload(null);
        }
      } finally {
        if (!silent) {
          setLoading(false);
        }
      }
    },
    [turnoSimuladoEnvio]
  );

  useEffect(() => {
    if (lojaIdControlada != null) {
      setSelectedSetorId(undefined);
      setSelectedLojaId(lojaIdControlada);
    }
  }, [lojaIdControlada]);

  useEffect(() => {
    if (dataFichaControlada) {
      setSelectedSetorId(undefined);
      setDataFicha(dataFichaControlada);
    }
  }, [dataFichaControlada]);

  useEffect(() => {
    if (turnoSimuladoControlado == null) return;
    setTurnoSimulado(turnoSimuladoControlado);
    if (turnoSimuladoControlado === "noite") {
      setGestaoNoGeral(false);
      setSelectedSetorId(undefined);
    } else {
      setGestaoNoGeral(true);
    }
  }, [turnoSimuladoControlado]);

  useEffect(() => {
    void loadFicha(dataFicha, selectedLojaId, selectedSetorId);
  }, [dataFicha, selectedLojaId, selectedSetorId, loadFicha]);

  useEffect(() => {
    setAssinaturaDesenhada(false);
    setAssinaturaImagem(null);
  }, [modalAssinatura]);

  useEffect(() => {
    const onFocusIn = (event: FocusEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;
      const tag = target.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") {
        setTecladoAberto(true);
      }
    };
    const onFocusOut = () => {
      window.setTimeout(() => {
        const active = document.activeElement as HTMLElement | null;
        const tag = active?.tagName;
        if (tag !== "INPUT" && tag !== "TEXTAREA" && tag !== "SELECT") {
          setTecladoAberto(false);
        }
      }, 50);
    };
    window.addEventListener("focusin", onFocusIn);
    window.addEventListener("focusout", onFocusOut);
    return () => {
      window.removeEventListener("focusin", onFocusIn);
      window.removeEventListener("focusout", onFocusOut);
    };
  }, []);

  useEffect(() => {
    const hasTicking = payload?.itens?.some((item) => {
      const status = item.cronometragem?.status;
      return status === "RUNNING" || status === "PAUSED";
    });
    if (!hasTicking) return;
    const timer = window.setInterval(() => setNowTick(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [payload?.itens]);

  useEffect(() => {
    void lojaService
      .getLojas()
      .then(setLojas)
      .catch(() => setLojas([]));
  }, []);

  useEffect(() => {
    if (selectedLojaId != null) return;
    if (user?.lojaId != null && user.lojaId >= 1) {
      setSelectedLojaId(user.lojaId);
    }
  }, [user?.lojaId, selectedLojaId]);

  const bloqueada = payload?.bloqueada === true;
  const baseConfirmada = payload?.baseConfirmada === true;
  const permiteEdicaoServidor = payload?.permiteEdicao === true;
  const podeAjustarFicha = payload?.podeAjustarFicha === true;
  const podeEditarBase = payload?.podeEditarBase === true;
  const podeAlterarUnd = payload?.podeAlterarUnd === true;
  const podeEditarSobra = payload?.podeEditarSobra === true;
  const podeEditarProducao = payload?.podeEditarProducao === true;
  const podeEditarObservacao = payload?.podeEditarObservacao === true;
  const podeAssinarProducao = payload?.podeAssinarProducao === true;
  const podeAssinarConferencia = payload?.podeAssinarConferencia === true;
  const podeAssinarProducaoTotal = payload?.podeAssinarProducaoTotal === true;
  const turnoAtual = payload?.turnoAtual ?? getTurnoProducao();
  const ficha = payload?.ficha;
  const edicaoLiberadaAtiva =
    payload?.edicaoLiberadaAtiva === true ||
    ficha?.edicaoLiberadaAtiva === true;
  const exigeSetor = payload?.exigeSetor === true;
  const setorAtual = payload?.setorAtual ?? null;
  const temResumoGestao = (payload?.resumoSetores?.length ?? 0) > 1;
  const podeVerFichaCompleta = podeVerFichaCompletaUser(user);
  const verMaisInfoFichaProducao = podeVerMaisInfoFichaProducaoUser(user);
  const mostrarVisaoGeral = temResumoGestao && gestaoNoGeral;

  useRankingEvents("ficha-diaria", selectedLojaId, {
    queryKeys: [],
    enabled: Boolean(selectedLojaId) && !fichaHistorica,
    onEvent: () => {
      if (saveInFlightRef.current) return;
      void loadFicha(dataFicha, selectedLojaId, selectedSetorId, {
        silent: true,
      });
    },
  });

  useEffect(() => {
    if (turnoAtual === "noite" || fichaHistorica) {
      setGestaoNoGeral(false);
    }
    if (fichaHistorica) {
      setModalFichaCompleta(false);
    }
  }, [turnoAtual, dataFicha, selectedLojaId, fichaHistorica]);

  useEffect(() => {
    if (!fichaHistorica && turnoAtual === "dia" && selectedSetorId == null) {
      setGestaoNoGeral(true);
    }
  }, [turnoAtual, selectedSetorId, fichaHistorica]);
  const assinaturaSetorAtual = useMemo(
    () =>
      payload?.assinaturasSetores?.find(
        (s) => s.setorId === (setorAtual?.id ?? selectedSetorId)
      ) ?? null,
    [payload?.assinaturasSetores, setorAtual?.id, selectedSetorId]
  );
  const liberadoDevConferencia = ficha?.liberadoDevConferencia === true;
  const liberadoDevProducao = ficha?.liberadoDevProducao === true;
  const setorLiberadoDev = assinaturaSetorAtual?.liberadoDev === true;
  const producaoLiberadaDev = liberadoDevProducao || setorLiberadoDev;
  const producaoAssinada =
    turnoAtual === "dia"
      ? assinaturaSetorAtual?.assinado === true
      : ficha?.assinadoProducao === true;
  const producaoTravadaPorAssinatura = producaoAssinada && !podeEditarProducao;
  const turnoEhNoite = turnoAtual === "noite";
  const turnoEhDia = turnoAtual === "dia";
  const fichaConsultaPorSetor = payload?.fichaConsultaPorSetor === true;
  const itensTemSetores = (payload?.itens ?? []).some(
    (item) => (item.setores?.length ?? 0) > 0
  );
  const dataProducaoAnterior = addDaysISO(dataFichaOperacional, -1);
  const vendoProducaoDoDiaAnterior =
    fichaHistorica && dataFicha === dataProducaoAnterior;
  // Conferência editável só na data operacional à noite. Histórico = ficha de produção.
  // Edição liberada 3h altera permissões no backend, não o layout do turno DIA.
  const layoutFichaUnificada = turnoEhNoite && !fichaHistorica;
  const mostrarCamposBaseSobra =
    layoutFichaUnificada ||
    verMaisInfoFichaProducao ||
    payload?.tipoDia === "sobra";
  const agruparPorSetor =
    !edicaoLiberadaAtiva &&
    !layoutFichaUnificada &&
    (fichaConsultaPorSetor || (fichaHistorica && itensTemSetores));
  const mostraCronometro =
    !layoutFichaUnificada && (exigeSetor ? selectedSetorId != null : true);
  const cronometroSomenteLeitura = !podeEditarProducao;
  // Na conferência, produção só aparece se o usuário for para o dia anterior.
  const mostraLancamentoProducao = !layoutFichaUnificada;
  const mostraProduziu = !layoutFichaUnificada;
  const mostraPausas = turnoEhDia && podeVerPausas && !fichaHistorica;
  const colunasTabelaFicha = useMemo(() => {
    let n = 1; // item
    if (mostrarCamposBaseSobra) n += 1; // base
    if (layoutFichaUnificada) {
      if (mostraLancamentoProducao) n += 1; // produções
      n += 1; // sobras
      if (mostraLancamentoProducao) n += 1; // obs. produção
      n += 1; // obs. sobra
    } else {
      if (mostrarCamposBaseSobra) n += 1; // sobras
      if (mostraLancamentoProducao) n += 1; // produções
      if (mostraProduziu) n += 1; // produziu
      n += 1; // obs.
    }
    if (mostraCronometro) n += 1;
    return n;
  }, [
    layoutFichaUnificada,
    mostrarCamposBaseSobra,
    mostraLancamentoProducao,
    mostraProduziu,
    mostraCronometro,
  ]);
  const horarioTurnoAtual = useMemo(
    () => formatHorarioTurnoAtual(turnoAtual, payload?.horariosTurno),
    [turnoAtual, payload?.horariosTurno]
  );

  const podeSalvar =
    (podeAjustarFicha || !bloqueada) &&
    (podeEditarSobra ||
      podeEditarProducao ||
      podeEditarObservacao ||
      podeEditarBase);
  const exibirTabelaFicha = (payload?.itens?.length ?? 0) > 0;

  const itensFiltrados = useMemo(() => {
    const itens = payload?.itens ?? [];
    const termo = normalizarBuscaItem(buscaItem);
    if (!termo) return itens;
    return itens.filter((item) =>
      normalizarBuscaItem(item.descricao).includes(termo)
    );
  }, [payload?.itens, buscaItem]);

  const blocosFicha = useMemo(() => {
    if (!agruparPorSetor) {
      return [{ setorId: null, setorNome: null, itens: itensFiltrados }];
    }
    return agruparItensFichaPorSetor(
      itensFiltrados,
      payload?.resumoSetores,
      unds
    );
  }, [
    agruparPorSetor,
    itensFiltrados,
    payload?.resumoSetores,
    unds,
  ]);

  useEffect(() => {
    linhasRef.current = linhas;
  }, [linhas]);
  const itensFichaRef = useRef(payload?.itens ?? []);
  itensFichaRef.current = payload?.itens ?? [];

  const buildLinhasPayload = useCallback(
    (snapshot: Record<number, LinhaLocal>) => {
      if (!payload?.itens) return [];
      return payload.itens.map((item) => {
        const key = item.lojaIdOutra ? item.itemIdOutraLoja! : item.itemId;
        const local = snapshot[key];
        const undSobra = undFichaDoItem(item, local?.undMedidaIdSobra);
        const undProducao = undFichaDoItem(item, local?.undMedidaIdProducao);
        const linha: {
          itemId: number;
          sobra: string;
          producao: string;
          producaoChecada: boolean;
          naoProduzidoPelaUnidade: boolean;
          justificativaNaoProduziu: string;
          observacaoSobra: string;
          observacaoProducao: string;
          undMedidaIdSobra?: number;
          undMedidaIdProducao?: number;
          quantidadeBase?: string;
          undMedidaIdBase?: number;
          lojaIdOutra?: number;
          itemIdOutraLoja?: number;
        } = {
          itemId: item.lojaIdOutra ? item.itemIdOutraLoja! : item.itemId,
          sobra: local?.sobra ?? "",
          producao: local?.producao ?? "",
          producaoChecada: local?.producaoChecada ?? false,
          naoProduzidoPelaUnidade: local?.naoProduzidoPelaUnidade ?? false,
          justificativaNaoProduziu: local?.justificativaNaoProduziu ?? "",
          observacaoSobra: local?.observacaoSobra ?? "",
          observacaoProducao: local?.observacaoProducao ?? "",
        };
        if (item.lojaIdOutra) {
          linha.lojaIdOutra = item.lojaIdOutra;
          linha.itemIdOutraLoja = item.itemIdOutraLoja;
        }
        if (podeEditarBase) {
          linha.quantidadeBase = local?.baseValor ?? "";
          const undBase = undFichaDoItem(item, local?.undMedidaIdBase);
          if (undBase !== "" && undBase != null && undBase >= 1) {
            linha.undMedidaIdBase = undBase;
          }
        }
        if (
          podeAlterarUnd &&
          podeEditarSobra &&
          undSobra !== "" &&
          undSobra != null &&
          undSobra >= 1
        ) {
          linha.undMedidaIdSobra = undSobra;
        }
        if (
          podeAlterarUnd &&
          podeEditarProducao &&
          undProducao !== "" &&
          undProducao != null &&
          undProducao >= 1
        ) {
          linha.undMedidaIdProducao = undProducao;
        }
        return linha;
      });
    },
    [
      payload?.itens,
      podeEditarSobra,
      podeEditarProducao,
      podeEditarBase,
      podeAlterarUnd,
    ]
  );

  const sincronizarPayloadComLinhas = useCallback(
    (
      snapshot: Record<number, LinhaLocal>,
      cronometragensSalvas?: Array<
        CronometragemItem & { itemId: number }
      >
    ) => {
      const nowMs = Date.now();
      const cronoPorItem = new Map(
        (cronometragensSalvas ?? []).map((row) => [row.itemId, row] as const)
      );
      setPayload((prev) => {
        if (!prev?.itens) return prev;
        return {
          ...prev,
          itens: prev.itens.map((item) => {
            const linhaKey = item.lojaIdOutra
              ? item.itemIdOutraLoja!
              : item.itemId;
            const local = snapshot[linhaKey];
            if (!local) return item;
            const doServidor = item.lojaIdOutra
              ? undefined
              : cronoPorItem.get(item.itemId);
            let cronometragem = item.cronometragem;
            if (item.lojaIdOutra) {
              cronometragem = null;
            } else if (doServidor) {
              cronometragem = {
                status: doServidor.status,
                startedAt: doServidor.startedAt,
                pausedAt: doServidor.pausedAt,
                finishedAt: doServidor.finishedAt,
                runningSince: doServidor.runningSince,
                tempoLiquidoMs: doServidor.tempoLiquidoMs,
                tempoFormatado: doServidor.tempoFormatado,
                tempoPausaManualMs: doServidor.tempoPausaManualMs,
                tempoPausaFormatado: doServidor.tempoPausaFormatado,
                pausaAutomatica: doServidor.pausaAutomatica,
                userId: doServidor.userId,
                userNome: doServidor.userNome,
              };
            } else if (
              local.producaoChecada &&
              !grupoTemOutraLojaPendente(
                item.itemId,
                snapshot,
                prev.itens ?? []
              )
            ) {
              cronometragem = cronometragemEncerradaSeProduziu(
                item.cronometragem,
                nowMs
              );
            }
            return {
              ...item,
              sobra: local.sobra,
              producao: local.producao,
              producaoChecada: local.producaoChecada,
              naoProduzidoPelaUnidade: local.naoProduzidoPelaUnidade,
              observacaoSobra: local.observacaoSobra,
              observacaoProducao: local.observacaoProducao,
              cronometragem,
            };
          }),
        };
      });
      payloadLoadedAtRef.current = nowMs;
      setNowTick(nowMs);
    },
    []
  );

  const executarSalvamento = useCallback(
    (opts?: { silent?: boolean }) => {
      if (!payload?.itens || !podeSalvar) {
        return Promise.resolve();
      }
      const silent = opts?.silent === true;
      const run = async () => {
        const snapshot = linhasRef.current;
        const dirtyIds = new Set(dirtyItemIdsRef.current);
        if (dirtyIds.size === 0) return;
        const todas = buildLinhasPayload(snapshot);
        const linhasEnvio = todas.filter((linha) => dirtyIds.has(linha.itemId));
        if (linhasEnvio.length === 0) return;
        if (silent) setAutoSaving(true);
        else setSavingManual(true);
        saveInFlightRef.current = true;
        try {
          const salvo = await controleProducaoService.saveFichaDiaria({
            dataFicha,
            lojaId: selectedLojaId,
            setorId: selectedSetorId,
            linhas: linhasEnvio,
            ...(turnoSimuladoEnvio
              ? { turnoSimulado: turnoSimuladoEnvio }
              : {}),
          });
          dirtyIds.forEach((id) => dirtyItemIdsRef.current.delete(id));
          linhasServidorRef.current = { ...snapshot };
          if (dirtyItemIdsRef.current.size === 0) {
            linhasAlteradasPeloUsuarioRef.current = false;
          }
          sincronizarPayloadComLinhas(snapshot, salvo.cronometragens);
          if (!silent) {
            toast.success("Ficha salva com sucesso.");
          }
        } catch (error: any) {
          setLinhas(linhasServidorRef.current);
          linhasRef.current = linhasServidorRef.current;
          dirtyItemIdsRef.current = new Set();
          linhasAlteradasPeloUsuarioRef.current = false;
          toast.error(error?.message || "Erro ao salvar ficha.");
          void loadFicha(dataFicha, selectedLojaId, selectedSetorId, {
            silent: true,
          });
          throw error;
        } finally {
          saveInFlightRef.current = false;
          if (silent) setAutoSaving(false);
          else setSavingManual(false);
        }
      };
      const chained = saveChainRef.current.then(run);
      saveChainRef.current = chained.catch(() => undefined);
      return chained;
    },
    [
      payload?.itens,
      podeSalvar,
      dataFicha,
      selectedLojaId,
      selectedSetorId,
      turnoSimuladoEnvio,
      buildLinhasPayload,
      sincronizarPayloadComLinhas,
      loadFicha,
    ]
  );

  const saveFicha = useCallback(
    async (opts?: { silent?: boolean }) => {
      await executarSalvamento(opts);
    },
    [executarSalvamento]
  );

  useEffect(() => {
    if (loading || !payload?.itens?.length) return;
    if (!podeSalvar) return;
    if (!linhasAlteradasPeloUsuarioRef.current) return;
    if (skipNextAutosaveRef.current) {
      skipNextAutosaveRef.current = false;
      return;
    }
    const timer = window.setTimeout(() => {
      void saveFicha({ silent: true });
    }, 400);
    return () => window.clearTimeout(timer);
  }, [
    linhas,
    loading,
    podeSalvar,
    payload?.itens,
    saveFicha,
    dataFicha,
    selectedLojaId,
    selectedSetorId,
  ]);

  useEffect(() => {
    const bumpTick = () => setNowTick(Date.now());
    const recarregarDoServidor = () => {
      bumpTick();
      if (!saveInFlightRef.current) {
        void loadFicha(dataFicha, selectedLojaId, selectedSetorId, {
          silent: true,
        });
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") recarregarDoServidor();
    };
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) recarregarDoServidor();
      else bumpTick();
    };
    window.addEventListener("pageshow", onPageShow);
    window.addEventListener("focus", recarregarDoServidor);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("pageshow", onPageShow);
      window.removeEventListener("focus", recarregarDoServidor);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [dataFicha, selectedLojaId, selectedSetorId, loadFicha]);

  const fichaProntaParaAssinarProducao = useMemo(() => {
    if (producaoLiberadaDev) return true;
    if (!payload?.itens?.length) return false;
    return payload.itens.every((item) => {
      if (item.lojaIdOutra) return true;
      const local = linhas[item.itemId];
      if (!linhaProducaoResolvida(local)) return false;
      if (local?.naoProduzidoPelaUnidade) return true;
      if (!local?.producaoChecada) return true;
      const status = item.cronometragem?.status;
      return status !== "RUNNING" && status !== "PAUSED";
    });
  }, [payload?.itens, linhas, producaoLiberadaDev]);

  const itensPendentesProducao = useMemo(() => {
    if (!payload?.itens?.length) return 0;
    return payload.itens.filter(
      (item) => !item.lojaIdOutra && !linhaProducaoResolvida(linhas[item.itemId])
    ).length;
  }, [payload?.itens, linhas]);

  const motivosAssinaturaProducao = useMemo(() => {
    if (producaoLiberadaDev) return [];
    const motivos: string[] = [];
    if (podeAssinarProducao && !ficha?.assinadoConfere) {
      motivos.push("Assine a conferência antes de assinar a produção.");
    }
    for (const item of payload?.itens ?? []) {
      if (item.lojaIdOutra) continue;
      const local = linhas[item.itemId];
      const nome = item.descricao;
      if (!linhaProducaoResolvida(local)) {
        if (local?.producaoChecada && !(local.producao ?? "").trim()) {
          motivos.push(`${nome}: informe a quantidade produzida.`);
        } else {
          motivos.push(
            `${nome}: marque Produziu ou justifique na observação.`
          );
        }
        continue;
      }
      if (local?.naoProduzidoPelaUnidade || !local?.producaoChecada) continue;
      const status = item.cronometragem?.status;
      if (status === "RUNNING") {
        motivos.push(`${nome}: cronômetro ainda andando — marque Produziu para encerrar.`);
      } else if (status === "PAUSED") {
        motivos.push(`${nome}: cronômetro pausado — retome ou marque Produziu para encerrar.`);
      }
    }
    return motivos;
  }, [
    payload?.itens,
    linhas,
    podeAssinarProducao,
    ficha?.assinadoConfere,
    producaoLiberadaDev,
  ]);

  const todosItensComSobra = useMemo(() => {
    if (!payload?.itens?.length) return false;
    return payload.itens.every((item) =>
      linhaTemSobra(linhas[item.itemId]?.sobra)
    );
  }, [payload?.itens, linhas]);

  const nenhumItemComSobra = useMemo(() => {
    if (!payload?.itens?.length) return false;
    return payload.itens.every(
      (item) => !linhaTemSobra(linhas[item.itemId]?.sobra)
    );
  }, [payload?.itens, linhas]);

  const prontaParaAssinarConferencia = useMemo(
    () =>
      liberadoDevConferencia ||
      todosItensComSobra ||
      nenhumItemComSobra,
    [liberadoDevConferencia, todosItensComSobra, nenhumItemComSobra]
  );

  const motivosAssinaturaConferencia = useMemo(() => {
    if (liberadoDevConferencia || prontaParaAssinarConferencia || !payload?.itens?.length) {
      return [];
    }
    const comSobra = payload.itens.filter((item) =>
      linhaTemSobra(linhas[item.itemId]?.sobra)
    ).length;
    const semSobra = payload.itens.length - comSobra;
    return [
      `Sobra parcial: ${comSobra} com sobra e ${semSobra} em branco. Preencha todas as linhas ou deixe todas vazias.`,
    ];
  }, [liberadoDevConferencia, prontaParaAssinarConferencia, payload?.itens, linhas]);

  const conferenciaParcialSobra = useMemo(
    () =>
      Boolean(payload?.itens?.length) &&
      !todosItensComSobra &&
      !nenhumItemComSobra,
    [payload?.itens, todosItensComSobra, nenhumItemComSobra]
  );

  const persistirLinhas = useCallback(
    async (nextLinhas: Record<number, LinhaLocal>) => {
      if (!payload?.itens) return;
      if (!podeSalvar) {
        throw new Error("Esta ficha não pode ser alterada no momento.");
      }
      linhasRef.current = nextLinhas;
      await executarSalvamento({ silent: true });
    },
    [payload?.itens, podeSalvar, executarSalvamento]
  );

  const atualizarLinha = useCallback(
    (
      itemId: number,
      patch: Partial<
        Pick<
          LinhaLocal,
          | "baseValor"
          | "undMedidaIdBase"
          | "sobra"
          | "undMedidaIdSobra"
          | "producao"
          | "undMedidaIdProducao"
          | "producaoChecada"
          | "naoProduzidoPelaUnidade"
          | "justificativaNaoProduziu"
          | "observacaoSobra"
          | "observacaoProducao"
        >
      >,
      opts?: { salvarAgora?: boolean }
    ) => {
      linhasAlteradasPeloUsuarioRef.current = true;
      dirtyItemIdsRef.current.add(itemId);
      const salvarAgora = opts?.salvarAgora === true;
      setLinhas((prev) => {
        const nextLinhas = {
          ...prev,
          [itemId]: {
            itemId,
            baseValor:
              patch.baseValor !== undefined
                ? patch.baseValor
                : (prev[itemId]?.baseValor ?? ""),
            undMedidaIdBase:
              patch.undMedidaIdBase !== undefined
                ? patch.undMedidaIdBase
                : (prev[itemId]?.undMedidaIdBase ?? ""),
            sobra:
              patch.sobra !== undefined
                ? patch.sobra
                : (prev[itemId]?.sobra ?? ""),
            undMedidaIdSobra:
              patch.undMedidaIdSobra !== undefined
                ? patch.undMedidaIdSobra
                : (prev[itemId]?.undMedidaIdSobra ?? ""),
            producao:
              patch.producao !== undefined
                ? patch.producao
                : (prev[itemId]?.producao ?? ""),
            undMedidaIdProducao:
              patch.undMedidaIdProducao !== undefined
                ? patch.undMedidaIdProducao
                : (prev[itemId]?.undMedidaIdProducao ?? ""),
            producaoChecada:
              patch.producaoChecada !== undefined
                ? patch.producaoChecada
                : (prev[itemId]?.producaoChecada ?? false),
            naoProduzidoPelaUnidade:
              patch.naoProduzidoPelaUnidade !== undefined
                ? patch.naoProduzidoPelaUnidade
                : (prev[itemId]?.naoProduzidoPelaUnidade ?? false),
            justificativaNaoProduziu:
              patch.justificativaNaoProduziu !== undefined
                ? patch.justificativaNaoProduziu
                : (prev[itemId]?.justificativaNaoProduziu ?? ""),
            observacaoSobra:
              patch.observacaoSobra !== undefined
                ? patch.observacaoSobra
                : (prev[itemId]?.observacaoSobra ?? ""),
            observacaoProducao:
              patch.observacaoProducao !== undefined
                ? patch.observacaoProducao
                : (prev[itemId]?.observacaoProducao ?? ""),
          },
        };
        if (salvarAgora) {
          skipNextAutosaveRef.current = true;
          linhasRef.current = nextLinhas;
          void persistirLinhas(nextLinhas).catch((error: unknown) => {
            const message =
              error instanceof Error ? error.message : "Erro ao salvar ficha.";
            toast.error(message);
          });
        }
        return nextLinhas;
      });
    },
    [persistirLinhas]
  );

  const alternarProducaoChecada = useCallback(
    (
      itemId: number,
      checada: boolean,
      sugestao?: string,
      sugestaoUndMedidaId?: number | null
    ) => {
      const atual = linhas[itemId];
      const patch: Partial<LinhaLocal> = {
        producaoChecada: checada,
        naoProduzidoPelaUnidade: false,
      };
      if (checada && !(atual?.producao ?? "").trim() && sugestao) {
        patch.producao = sugestao;
      }
      if (checada && sugestaoUndMedidaId != null && sugestaoUndMedidaId >= 1) {
        patch.undMedidaIdProducao = sugestaoUndMedidaId;
      }
      atualizarLinha(itemId, patch, { salvarAgora: true });
    },
    [atualizarLinha, linhas]
  );

  const executarAssinaturaProducao = async () => {
    if (!producaoLiberadaDev && !ficha?.assinadoConfere) {
      toast.warning("Assine a conferência antes de assinar a produção.");
      return;
    }
    if (exigeSetor && !selectedSetorId) {
      toast.warning("Selecione o setor para assinar a produção.");
      return;
    }
    if (!fichaProntaParaAssinarProducao) {
      toast.warning(
        "Conclua todos os itens: marque como produzido, não produzido pela unidade, ou informe justificativa na observação dos itens não produzidos."
      );
      return;
    }
    setAssinandoProducao(true);
    try {
      await controleProducaoService.assinarProducao(
        dataFicha,
        selectedLojaId,
        selectedSetorId,
        turnoSimuladoEnvio,
        assinaturaImagem ?? undefined
      );
      toast.success(
        exigeSetor ? "Produção do setor assinada." : "Produção assinada."
      );
      setModalAssinatura(null);
      await loadFicha(dataFicha, selectedLojaId, selectedSetorId, {
        silent: true,
      });
    } catch (error: any) {
      toast.error(error?.message || "Erro ao assinar produção.");
    } finally {
      setAssinandoProducao(false);
    }
  };

  const abrirModalAssinaturaConferencia = useCallback(() => {
    setJustificativaSemSobraDraft(
      ficha?.justificativaSemSobra?.trim() || JUSTIFICATIVA_SEM_SOBRA_PADRAO
    );
    setModalAssinatura("conferencia");
  }, [ficha?.justificativaSemSobra]);

  const montarInputSobrasPdf = useCallback((): FichaDiariaSobrasPdfInput | null => {
    if (!payload?.itens?.length) return null;
    return {
      lojaNome: nomeLojaSelecionada,
      dataISO: dataFicha,
      diaSemanaLabel:
        payload.diaSemanaLabel ?? labelDiaSemanaFromISO(dataFicha),
      itens: payload.itens,
      linhas,
      unds: payload.unds ?? [],
      justificativaSemSobra: nenhumItemComSobra
        ? justificativaSemSobraDraft.trim() ||
          ficha?.justificativaSemSobra ||
          null
        : ficha?.justificativaSemSobra ?? null,
    };
  }, [
    payload,
    nomeLojaSelecionada,
    dataFicha,
    linhas,
    nenhumItemComSobra,
    justificativaSemSobraDraft,
    ficha?.justificativaSemSobra,
  ]);

  const garantirRascunhoSalvoConferencia = useCallback(async () => {
    if (!podeSalvar) return;
    await saveFicha({ silent: true });
  }, [podeSalvar, saveFicha]);

  const verPdfSobrasConferencia = useCallback(async () => {
    const input = montarInputSobrasPdf();
    if (!input) {
      toast.warning("Não há itens para gerar o PDF das sobras.");
      return;
    }
    setPreparandoSobrasPdf(true);
    try {
      await garantirRascunhoSalvoConferencia();
      await abrirSobrasPdf(input);
    } catch (error: any) {
      toast.error(error?.message || "Não foi possível abrir o PDF das sobras.");
    } finally {
      setPreparandoSobrasPdf(false);
    }
  }, [montarInputSobrasPdf, garantirRascunhoSalvoConferencia]);

  const encaminharSobrasWhatsApp = useCallback(async () => {
    const input = montarInputSobrasPdf();
    if (!input) {
      toast.warning("Não há itens para encaminhar as sobras.");
      return;
    }
    setPreparandoSobrasPdf(true);
    try {
      await garantirRascunhoSalvoConferencia();
      const resultado = await compartilharSobrasPdfWhatsApp(input);
      if (resultado === "shared") {
        toast.success("PDF pronto para encaminhar no WhatsApp.");
      } else {
        toast.info(
          "PDF baixado. Anexe o arquivo na conversa do WhatsApp que abriu."
        );
      }
    } catch (error: any) {
      if (error?.name === "AbortError") return;
      toast.error(
        error?.message || "Não foi possível encaminhar o PDF no WhatsApp."
      );
    } finally {
      setPreparandoSobrasPdf(false);
    }
  }, [montarInputSobrasPdf, garantirRascunhoSalvoConferencia]);

  const executarAssinaturaConferencia = async () => {
    if (!liberadoDevConferencia && !prontaParaAssinarConferencia) {
      toast.warning(
        "Preencha SOBRA em todos os itens ou deixe todos em branco para assinar informando que não houve sobra."
      );
      return;
    }
    const justificativa = nenhumItemComSobra
      ? justificativaSemSobraDraft.trim()
      : undefined;
    if (nenhumItemComSobra && !justificativa) {
      toast.warning("Informe a justificativa de que não houve sobra.");
      return;
    }
    setAssinandoConferencia(true);
    try {
      await garantirRascunhoSalvoConferencia();
      await controleProducaoService.assinarConferencia(
        dataFicha,
        selectedLojaId,
        justificativa,
        turnoSimuladoEnvio,
        assinaturaImagem ?? undefined
      );
      toast.success("Conferência assinada.");
      setModalAssinatura(null);
      await loadFicha(dataFicha, selectedLojaId, selectedSetorId, {
        silent: true,
      });
    } catch (error: any) {
      toast.error(error?.message || "Erro ao assinar conferência.");
    } finally {
      setAssinandoConferencia(false);
    }
  };

  const executarAssinaturaProducaoTotal = async () => {
    setAssinandoProducaoTotal(true);
    try {
      const pendencias = payload?.pendenciasProducaoTotal;
      const temPendencias =
        (pendencias?.setoresSemAssinatura.length ?? 0) > 0 ||
        (pendencias?.itensPendentes.length ?? 0) > 0;
      await controleProducaoService.assinarProducaoTotal(
        dataFicha,
        selectedLojaId,
        turnoSimuladoEnvio,
        assinaturaImagem ?? undefined,
        temPendencias
      );
      toast.success("Produção total dos setores assinada.");
      setModalAssinatura(null);
      await loadFicha(dataFicha, selectedLojaId, selectedSetorId, {
        silent: true,
      });
    } catch (error: any) {
      toast.error(error?.message || "Erro ao Assinar produção setores.");
    } finally {
      setAssinandoProducaoTotal(false);
    }
  };

  const acionarCronometroItem = useCallback(
    async (
      itemId: number,
      acao: "iniciar" | "pausar" | "retomar",
      descricao?: string
    ) => {
      if (exigeSetor && !selectedSetorId) {
        toast.warning("Selecione o setor para usar o cronômetro.");
        return;
      }
      if (
        itemNaoUsaTimerNaFicha(
          itensFichaRef.current.find((i) => i.itemId === itemId)?.naoUsaTimer,
          dataFicha
        )
      ) {
        toast.warning("Este item não usa cronômetro.");
        return;
      }
      if (
        (acao === "iniciar" || acao === "retomar") &&
        linhasRef.current[itemId]?.naoProduzidoPelaUnidade
      ) {
        toast.warning(
          "Item não produzido pela unidade não gera cronômetro."
        );
        return;
      }
      if (
        (acao === "iniciar" || acao === "retomar") &&
        linhasRef.current[itemId]?.producaoChecada &&
        !grupoTemOutraLojaPendente(
          itemId,
          linhasRef.current,
          itensFichaRef.current
        )
      ) {
        toast.warning(
          "O cronômetro encerra ao marcar o item como produzido."
        );
        return;
      }

      let justificativa: string | undefined;
      if (acao === "retomar") {
        const cronoAtual = itensFichaRef.current.find(
          (i) => i.itemId === itemId && !i.lojaIdOutra
        )?.cronometragem;
        const motivo = await pedirJustificativaPausa(
          descricao,
          cronoAtual?.pausaAutomatica ? "Pausa automática" : undefined
        );
        if (!motivo) return;
        justificativa = motivo.trim().slice(0, 500);
      }

      try {
        const cronometragem = await controleProducaoService.acionarCronometro(
          acao,
          {
            dataFicha,
            ...(selectedSetorId != null ? { setorId: selectedSetorId } : {}),
            itemId,
            lojaId: selectedLojaId,
            ...(justificativa ? { justificativa } : {}),
            ...(turnoSimuladoEnvio
              ? { turnoSimulado: turnoSimuladoEnvio }
              : {}),
          }
        );
        const agora = Date.now();
        setPayload((prev) => {
          if (!prev?.itens) return prev;
          return {
            ...prev,
            itens: prev.itens.map((item) => {
              if (item.lojaIdOutra) {
                return { ...item, cronometragem: null };
              }
              if (item.itemId === itemId) {
                return { ...item, cronometragem };
              }
              if (
                (acao === "iniciar" || acao === "retomar") &&
                item.cronometragem?.status === "RUNNING"
              ) {
                const ticked =
                  cronometragemComTick(item.cronometragem, agora) ??
                  item.cronometragem;
                return {
                  ...item,
                  cronometragem: {
                    ...ticked,
                    status: "PAUSED",
                    runningSince: null,
                    pausedAt: new Date(agora).toISOString(),
                    pausaAutomatica: true,
                  },
                };
              }
              return item;
            }),
          };
        });
        payloadLoadedAtRef.current = agora;
        setNowTick(agora);
      } catch (error: any) {
        toast.error(error?.message || "Erro ao atualizar o cronômetro.");
      }
    },
    [dataFicha, selectedLojaId, selectedSetorId, turnoSimuladoEnvio, exigeSetor]
  );

  const assinaturaEmAndamento =
    assinandoProducao ||
    assinandoProducaoTotal ||
    assinandoConferencia ||
    preparandoSobrasPdf;

  const abrirModalObservacao = (
    itemId: number,
    descricao: string,
    tipo: ModalObservacaoTipo,
    opts?: { justificativaNaoProduzido?: boolean }
  ) => {
    const local = linhas[itemId];
    setObservacaoDraft(
      tipo === "sobra"
        ? (local?.observacaoSobra ?? "")
        : (local?.observacaoProducao ?? "")
    );
    setModalObservacao({
      itemId,
      descricao,
      tipo,
      justificativaNaoProduzido: opts?.justificativaNaoProduzido,
    });
  };

  const abrirModalJustificativaNaoProduzido = useCallback(
    (itemId: number, descricao: string) => {
      setObservacaoDraft(linhas[itemId]?.observacaoProducao ?? "");
      setModalObservacao({
        itemId,
        descricao,
        tipo: "producao",
        justificativaNaoProduzido: true,
      });
    },
    [linhas]
  );

  const handleToggleProduzido = useCallback(
    async (linhaKey: number, descricao: string, checada: boolean) => {
      const item = payload?.itens?.find((i) =>
        i.lojaIdOutra ? i.itemIdOutraLoja === linhaKey : i.itemId === linhaKey
      );
      if (checada) {
        const local = linhas[linhaKey];
        const sugestao = item ? sugestaoProducaoLinha(item, local, unds) : "";
        if (
          !itemNaoUsaTimerNaFicha(item?.naoUsaTimer, dataFicha) &&
          !sugestaoProducaoIsentaCronometro(sugestao)
        ) {
          const cronometragemGrupo = cronometragemDoGrupo(item, payload?.itens);
          const ticked = cronometragemComTick(
            cronometragemGrupo,
            Date.now()
          );
          const motivo = motivoBloqueioLancamentoProducao({
            status: ticked?.status,
            tempoExibidoMs: ticked?.tempoLiquidoMs ?? 0,
          });
          if (motivo) {
            toast.warning(motivo);
            return;
          }
        }
        const undSugestao = item
          ? undFichaDoItem(item, local?.undMedidaIdProducao)
          : "";
        const agora = Date.now();
        setPayload((prev) => {
          const itens = prev?.itens;
          if (!itens || !item || item.lojaIdOutra) return prev;
          return {
            ...prev,
            itens: itens.map((row) => {
              if (row.lojaIdOutra || row.itemId !== item.itemId) return row;
              if (grupoTemOutraLojaPendente(row.itemId, linhas, itens)) {
                return row;
              }
              return {
                ...row,
                cronometragem: cronometragemEncerradaSeProduziu(
                  row.cronometragem,
                  agora
                ),
              };
            }),
          };
        });
        setNowTick(agora);
        alternarProducaoChecada(
          linhaKey,
          true,
          sugestao,
          undSugestao !== "" ? undSugestao : item?.undMedidaIdBase
        );
        return;
      }

      const local = linhas[linhaKey];
      if (local?.producaoChecada) {
        const result = await Swal.fire({
          title: "Reverter marcação?",
          html: `O item <strong>${descricao}</strong> está marcado como produzido.<br/><br/>Deseja desfazer por engano ou informar que não foi produzido?`,
          icon: "question",
          showCancelButton: true,
          showDenyButton: true,
          confirmButtonText: "Desfazer marcação",
          denyButtonText: "Não foi produzido",
          cancelButtonText: "Cancelar",
          confirmButtonColor: COLORS.PRIMARY,
          denyButtonColor: COLORS.WARNING_DARK,
        });

        if (result.isConfirmed) {
          atualizarLinha(
            linhaKey,
            { producaoChecada: false },
            { salvarAgora: true }
          );
          return;
        }
        if (result.isDenied) {
          abrirModalJustificativaNaoProduzido(linhaKey, descricao);
        }
        return;
      }
    },
    [
      alternarProducaoChecada,
      abrirModalJustificativaNaoProduzido,
      atualizarLinha,
      payload?.itens,
      linhas,
      unds,
      dataFicha,
    ]
  );

  const handleNaoProduziu = useCallback(
    async (linhaKey: number, descricao: string) => {
      const { value: justificativa } = await Swal.fire({
        title: "Não produziu",
        html: `Informe o motivo de <strong>${descricao}</strong> não ter sido produzido:`,
        input: "textarea",
        inputPlaceholder: "Justificativa...",
        inputAttributes: { minlength: "3", maxlength: "500" },
        showCancelButton: true,
        confirmButtonText: "Confirmar",
        cancelButtonText: "Cancelar",
        confirmButtonColor: COLORS.PRIMARY,
        inputValidator: (v) =>
          !v || v.trim().length < 3
            ? "A justificativa deve ter no mínimo 3 caracteres."
            : null,
      });
      if (!justificativa) return;
      atualizarLinha(
        linhaKey,
        {
          naoProduzidoPelaUnidade: true,
          justificativaNaoProduziu: justificativa.trim(),
          producaoChecada: false,
          producao: "",
        },
        { salvarAgora: true },
      );
    },
    [atualizarLinha],
  );

  const salvarObservacaoModal = () => {
    if (!modalObservacao) return;
    const texto = observacaoDraft.trim();
    if (modalObservacao.justificativaNaoProduzido && !texto) {
      toast.warning("Informe o motivo de não ter produzido este item.");
      return;
    }
    const patch: Partial<LinhaLocal> =
      modalObservacao.tipo === "sobra"
        ? { observacaoSobra: observacaoDraft }
        : { observacaoProducao: observacaoDraft };
    if (modalObservacao.justificativaNaoProduzido) {
      patch.producaoChecada = false;
      patch.naoProduzidoPelaUnidade = false;
    }
    atualizarLinha(modalObservacao.itemId, patch, { salvarAgora: true });
    setModalObservacao(null);
  };

  const linhasTextareaObservacao = Math.min(
    14,
    Math.max(
      5,
      observacaoDraft.split("\n").length,
      Math.ceil(observacaoDraft.length / 48) || 1
    )
  );

  const statusAssinaturaInformativo = useMemo(() => {
    if (!baseConfirmada || !isFichaOperacionalAtual || !ficha) return null;

    if (ficha.assinadoConfere && ficha.assinadoProducao) {
      return {
        texto: "Conferência e produção total assinadas",
        tom: "emerald" as const,
      };
    }
    if (ficha.assinadoConfere && !ficha.assinadoProducao) {
      return {
        texto: "Conferência assinada · aguardando produção total",
        tom: "sky" as const,
      };
    }
    if (!ficha.assinadoConfere && ficha.assinadoProducao) {
      return {
        texto: "Produção assinada · aguardando conferência",
        tom: "sky" as const,
      };
    }
    return {
      texto: "Aguardando conferência e produção",
      tom: "amber" as const,
    };
  }, [baseConfirmada, ficha, isFichaOperacionalAtual]);

  const somenteLeitura = !bloqueada && !permiteEdicaoServidor;
  const diaSemanaLabel = useMemo(
    () => payload?.diaSemanaLabel ?? labelDiaSemanaFromISO(dataFicha),
    [payload?.diaSemanaLabel, dataFicha]
  );
  const dataBr = useMemo(
    () => BrazilDate.formatBR(BrazilDate.fromISOString(dataFicha), false),
    [dataFicha]
  );
  const segundaSemanaLabel = useMemo(() => {
    const iso = payload?.dataSegunda?.slice(0, 10) ?? mondayOfISO(dataFicha);
    return BrazilDate.formatBR(BrazilDate.fromISOString(iso), false);
  }, [payload?.dataSegunda, dataFicha]);
  const diaSemanaImpressao = useMemo(
    () => diaSemanaLabel.toUpperCase(),
    [diaSemanaLabel]
  );

  const contextMessages = useMemo((): FichaDiariaContextMessage[] => {
    const messages: FichaDiariaContextMessage[] = [];

    if (payload?.avisoItensSemSetor) {
      messages.push({
        id: "itens-sem-setor",
        tone: "warning",
        text: payload.avisoItensSemSetor,
      });
    }

    if (payload?.avisoDiaFechado) {
      messages.push({
        id: "dia-fechado",
        tone: "readonly",
        text: payload.avisoDiaFechado,
      });
    }

    if (payload?.avisoDiaSemProducao) {
      messages.push({
        id: "dia-sem-producao",
        tone: "warning",
        text: payload.avisoDiaSemProducao,
      });
    }

    if (edicaoLiberadaAtiva) {
      const ate = payload?.edicaoLiberadaAte ?? ficha?.edicaoLiberadaAte;
      const ateFmt = ate
        ? new Date(ate).toLocaleString("pt-BR", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })
        : null;
      messages.push({
        id: "edicao-liberada",
        tone: "info",
        text: (
          <>
            Edição liberada temporariamente
            {ateFmt ? (
              <>
                {" "}
                até <strong>{ateFmt}</strong>
              </>
            ) : null}
            {payload?.edicaoLiberadaPorNome || ficha?.edicaoLiberadaPorNome ? (
              <>
                {" "}
                (por{" "}
                {payload?.edicaoLiberadaPorNome ?? ficha?.edicaoLiberadaPorNome})
              </>
            ) : null}
            . Você pode lançar sobras, produção e observações
            {payload?.edicaoLiberadaEscopoLoja || ficha?.edicaoLiberadaEscopoLoja
              ? " em qualquer data desta loja."
              : " nesta data."}
          </>
        ),
      });
    }

    const producoesAbertas = (payload?.itens ?? []).filter((item) => {
      if (item.lojaIdOutra) return false;
      const local = linhas[item.itemId];
      if (local?.naoProduzidoPelaUnidade) return false;
      return local?.producaoChecada !== true;
    }).length;
    if (
      producoesAbertas > 0 &&
      !ficha?.assinadoProducao &&
      payload?.tipoDia === "producao"
    ) {
      messages.unshift({
        id: "producao-nao-finalizada",
        tone: "warning",
        text: (
          <>
            <strong>{producoesAbertas}</strong>{" "}
            {producoesAbertas === 1 ? "item ainda não teve" : "itens ainda não tiveram"}{" "}
            a produção finalizada. O campo fica em destaque até marcar como
            produzido.
          </>
        ),
      });
    }

    if (payload?.avisoSobraReferencia && payload.tipoDia === "producao") {
      messages.push({
        id: "sobra-referencia",
        tone: "info",
        text: payload.avisoSobraReferencia,
      });
    }

    if (isAdmin && payload?.mensagem && payload.tipoDia === "producao") {
      messages.push({
        id: "admin-mensagem",
        tone: "warning",
        text: payload.mensagem,
      });
    }

    if (isAdmin && !baseConfirmada && payload?.avisoBaseDia) {
      messages.push({
        id: "aviso-base",
        tone: "warning",
        text: payload.avisoBaseDia,
      });
    }

    if (somenteLeitura && !edicaoLiberadaAtiva) {
      messages.push({
        id: "somente-leitura",
        tone: "readonly",
        text: vendoProducaoDoDiaAnterior ? (
          <>
            Produção do dia anterior, em consulta: dados reais por setor,
            status e cronômetro. Para lançar sobras, volte à conferência de{" "}
            <strong>
              {BrazilDate.formatBR(
                BrazilDate.fromISOString(dataFichaOperacional),
                false
              )}
            </strong>
            .
          </>
        ) : (
          <>
            Modo somente leitura: esta data não está liberada no turno atual.
            Ficha operacional sugerida:{" "}
            <strong>
              {BrazilDate.formatBR(
                BrazilDate.fromISOString(dataFichaOperacional),
                false
              )}
            </strong>
            . Demais datas podem ser consultadas.
          </>
        ),
      });
    }

    if (!podeEditarProducao && payload?.motivoBloqueioProducao) {
      messages.push({
        id: "bloqueio-producao",
        tone: "warning",
        text: `EDIÇÃO BLOQUEADA: ${payload.motivoBloqueioProducao}`,
      });
    }

    if (
      podeAssinarConferencia &&
      exibirTabelaFicha &&
      !ficha?.assinadoConfere
    ) {
      if (conferenciaParcialSobra) {
        messages.push({
          id: "conferencia-parcial",
          tone: "warning",
          text: (
            <>
              Para assinar a conferência, preencha <strong>SOBRA</strong> em
              todas as linhas ou deixe todas em branco e assine informando que{" "}
              <strong>não houve sobra</strong>.
            </>
          ),
        });
      } else if (nenhumItemComSobra) {
        messages.push({
          id: "conferencia-sem-sobra",
          tone: "guidance",
          text: (
            <>
              Nenhuma sobra lançada — você pode assinar a conferência
              informando que não houve sobra neste dia.
            </>
          ),
        });
      }

      if (turnoEhDia && !ficha?.assinadoProducao) {
        messages.push({
          id: "conferencia-antes-producao",
          tone: "guidance",
          text: (
            <>
              Assine a <strong>conferência</strong> antes do lançamento e da
              assinatura de produção (pode ser feito no turno diurno).
            </>
          ),
        });
      }
    }

    if (
      podeAssinarProducao &&
      !fichaProntaParaAssinarProducao &&
      exibirTabelaFicha
    ) {
      messages.push({
        id: "producao-pendente",
        tone: "action",
        text: (
          <>
            Para assinar a produção deste setor, cada item deve estar{" "}
            <strong>marcado como produzido</strong> (com quantidade),{" "}
            <strong>não produzido pela unidade</strong> ou ter{" "}
            <strong>justificativa na observação</strong> quando não for
            produzido. Encerre o cronômetro dos itens produzidos.
            {itensPendentesProducao > 0 && (
              <>
                {" "}
                Pendentes: <strong>{itensPendentesProducao}</strong>.
              </>
            )}
          </>
        ),
      });
    }

    return messages;
  }, [
    payload,
    isAdmin,
    baseConfirmada,
    somenteLeitura,
    edicaoLiberadaAtiva,
    ficha,
    vendoProducaoDoDiaAnterior,
    dataFichaOperacional,
    podeAssinarConferencia,
    exibirTabelaFicha,
    ficha?.assinadoConfere,
    ficha?.assinadoProducao,
    conferenciaParcialSobra,
    nenhumItemComSobra,
    turnoEhDia,
    podeAssinarProducao,
    fichaProntaParaAssinarProducao,
    itensPendentesProducao,
    linhas,
  ]);

  const statusAssinaturaBadgeClass = useMemo(() => {
    if (!statusAssinaturaInformativo) return "";
    switch (statusAssinaturaInformativo.tom) {
      case "emerald":
        return "bg-emerald-100 text-emerald-800";
      case "sky":
        return "bg-sky-100 text-sky-800";
      default:
        return "bg-amber-100 text-amber-800";
    }
  }, [statusAssinaturaInformativo]);

  const handleVerPausas = useCallback(async () => {
    setLoadingPausas(true);
    setPausasModalOpen(true);
    try {
      const result = await controleProducaoService.listarPausasFicha({
        data: dataFicha,
        lojaId: selectedLojaId,
        setorId: setorAtual?.id,
      });
      setPausas(result);
    } catch {
      setPausas([]);
    } finally {
      setLoadingPausas(false);
    }
  }, [dataFicha, selectedLojaId, setorAtual?.id]);

  const imprimirFicha = useCallback(() => {
    const tituloOriginal = document.title;
    document.title = tituloPdfFichaProducao(
      dataFicha,
      nomeLojaSelecionada,
      setorAtual?.nome,
      layoutFichaUnificada
    );

    /** Altura útil A4 retrato (~297mm − margens 7mm × 2). */
    const alturaUtilA4Px = Math.round((297 - 14) * 3.7795275591);

    const aguardarImagensPrint = () => {
      const root = document.querySelector<HTMLElement>(".ficha-print-root");
      if (!root) return Promise.resolve();
      const imgs = Array.from(root.querySelectorAll("img"));
      return Promise.all(
        imgs.map(
          (img) =>
            img.complete
              ? Promise.resolve()
              : new Promise<void>((resolve) => {
                  const done = () => resolve();
                  img.addEventListener("load", done, { once: true });
                  img.addEventListener("error", done, { once: true });
                  window.setTimeout(done, 2500);
                })
        )
      );
    };

    const ajustarEscalaUmaPagina = () => {
      const root = document.querySelector<HTMLElement>(".ficha-print-root");
      if (!root) return;
      root.style.transform = "";
      root.style.width = "";
      root.style.marginLeft = "";
      const altura = root.getBoundingClientRect().height || root.scrollHeight;
      if (altura > alturaUtilA4Px) {
        const escala = Math.max(0.88, Math.min(1, alturaUtilA4Px / altura));
        root.style.transform = `scale(${escala})`;
        root.style.transformOrigin = "top center";
        root.style.width = `${100 / escala}%`;
        root.style.marginLeft = `${((100 - 100 / escala) / 2).toFixed(4)}%`;
      }
    };

    const limparAjusteImpressao = () => {
      const root = document.querySelector<HTMLElement>(".ficha-print-root");
      if (root) {
        root.style.transform = "";
        root.style.width = "";
        root.style.marginLeft = "";
      }
      document.title = tituloOriginal;
      window.removeEventListener("beforeprint", ajustarEscalaUmaPagina);
      window.removeEventListener("afterprint", limparAjusteImpressao);
    };

    window.addEventListener("beforeprint", ajustarEscalaUmaPagina);
    window.addEventListener("afterprint", limparAjusteImpressao);
    void aguardarImagensPrint().then(() => {
      ajustarEscalaUmaPagina();
      window.print();
    });
  }, [dataFicha, nomeLojaSelecionada, setorAtual?.nome, layoutFichaUnificada]);

  return (
    <div
      className={`mx-auto max-w-7xl overflow-x-hidden px-3 py-3 sm:px-6 lg:px-8 lg:py-5 xl:max-w-none xl:overflow-visible ${
        tecladoAberto
          ? "pb-4"
          : exibirTabelaFicha
            ? "pb-[calc(5.5rem+env(safe-area-inset-bottom))] xl:pb-8"
            : "pb-6 xl:pb-8"
      }`}
    >
      <style>
        {`
          .print-only { display: none; }
          .ficha-print-root {
            font-family: Arial, Helvetica, sans-serif;
            color: #000;
            background: #fff;
            box-sizing: border-box;
          }
          .ficha-print-top {
            display: flex;
            align-items: flex-start;
            justify-content: space-between;
            gap: 10px;
            margin-bottom: 4px;
            padding-bottom: 4px;
            flex-shrink: 0;
          }
          .ficha-print-top-left {
            display: flex;
            align-items: center;
            gap: 10px;
            min-width: 0;
          }
          .ficha-print-logo {
            height: 46px;
            width: auto;
            object-fit: contain;
            flex-shrink: 0;
          }
          .ficha-print-title {
            margin: 0;
            font-size: 15px;
            font-weight: 700;
            letter-spacing: 0.02em;
            line-height: 1.2;
          }
          .ficha-print-subtitle {
            margin: 2px 0 0;
            font-size: 11px;
            font-weight: 400;
          }
          .ficha-print-top-right {
            text-align: right;
            flex-shrink: 0;
          }
          .ficha-print-weekday {
            font-size: 13px;
            font-weight: 700;
            text-transform: uppercase;
          }
          .ficha-print-date {
            margin-top: 2px;
            font-size: 12px;
          }
          .ficha-print-table {
            width: 100%;
            border-collapse: collapse;
            table-layout: fixed;
            font-size: 10px;
          }
          .ficha-print-table th,
          .ficha-print-table td {
            border: 1px solid #000;
            padding: 2px 4px;
            vertical-align: middle;
            word-wrap: break-word;
            line-height: 1.2;
          }
          .ficha-print-table thead th {
            background: #f3f3f3;
            font-weight: 700;
            text-align: center;
          }
          .ficha-print-th-desc { width: 34%; text-align: center !important; }
          .ficha-print-th-peso { width: 11%; }
          .ficha-print-th-qty-group { width: 27%; }
          .ficha-print-th-qty { width: 9%; }
          .ficha-print-th-qty-base { width: 11%; }
          .ficha-print-th-obs { width: 17%; }
          .ficha-print-th-tempo { width: 7%; }
          .ficha-print-td-desc { text-align: left; }
          .ficha-print-td-peso,
          .ficha-print-td-qty { text-align: center; }
          .ficha-print-td-qty-base { text-align: center; min-width: 4.5rem; }
          .ficha-print-td-obs { text-align: left; font-size: 9px; }
          .ficha-print-td-tempo { text-align: center; font-size: 9px; }
          .ficha-print-setor {
            background: #f8eaea;
            font-weight: 700;
            text-align: left;
            letter-spacing: 0.04em;
            text-transform: uppercase;
            font-size: 10px;
          }
          .ficha-print-lower {
            flex-shrink: 0;
          }
          .ficha-print-orientacoes {
            padding-top: 6px;
            border-top: 1px solid #ccc;
            text-align: center;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
          }
          .ficha-print-orientacoes-title {
            font-size: 12px;
            font-weight: 700;
            margin-bottom: 6px;
            text-align: center;
          }
          .ficha-print-orientacoes-texto {
            margin: 0 auto 10px;
            font-size: 11px;
            line-height: 1.45;
            white-space: pre-wrap;
            text-align: center;
            max-width: 100%;
          }
          .ficha-print-orientacoes-imagens {
            display: flex;
            flex-wrap: wrap;
            gap: 14px;
            align-items: center;
            justify-content: center;
            width: 100%;
          }
          .ficha-print-orientacoes-img {
            width: auto;
            height: auto;
            object-fit: contain;
            border: 1px solid #ccc;
          }
          .ficha-print-orientacoes--n1 .ficha-print-orientacoes-img {
            max-width: 220px;
            max-height: 220px;
          }
          .ficha-print-orientacoes--n2 .ficha-print-orientacoes-img {
            max-width: 200px;
            max-height: 200px;
          }
          .ficha-print-orientacoes--n3 .ficha-print-orientacoes-img {
            max-width: 185px;
            max-height: 185px;
          }
          .ficha-print-orientacoes--n4 .ficha-print-orientacoes-img {
            max-width: 145px;
            max-height: 145px;
          }
          .ficha-print-footer {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 14px;
            margin-top: 0;
            page-break-inside: avoid;
            break-inside: avoid;
            flex-shrink: 0;
          }
          .ficha-print-footer-label {
            font-size: 11px;
            font-weight: 700;
            text-align: center;
            margin-bottom: 6px;
          }
          .ficha-print-footer-cell {
            text-align: center;
            min-width: 0;
          }
          .ficha-print-assinatura {
            display: block;
            height: 42px;
            max-width: 100%;
            margin: 0 auto 4px;
            object-fit: contain;
            object-position: center;
            background: #fff;
          }
          .ficha-print-footer-value {
            font-size: 9px;
            text-align: center;
            line-height: 1.35;
          }
          .ficha-print-root--compact .ficha-print-table {
            font-size: 9px;
          }
          .ficha-print-root--compact .ficha-print-table th,
          .ficha-print-root--compact .ficha-print-table td {
            padding: 1px 3px;
          }
          .ficha-print-root--compact .ficha-print-logo {
            height: 40px;
          }
          .ficha-print-root--dense .ficha-print-table {
            font-size: 8.5px;
          }
          .ficha-print-footer-line {
            border-top: 1px solid #000;
            min-height: 1px;
          }
          .ficha-print-footer-signed {
            font-size: 10px;
            text-align: center;
            line-height: 1.4;
            padding-top: 5px;
            border-top: 1px solid #000;
          }
          @media print {
            @page {
              size: A4 portrait;
              margin: 7mm;
            }
            .no-print { display: none !important; }
            .print-only {
              display: block !important;
              height: 0;
              overflow: visible;
            }
            body {
              background: #fff !important;
              margin: 0 !important;
            }
            body * { visibility: hidden; }
            .ficha-print-root,
            .ficha-print-root * { visibility: visible; }
            .ficha-print-root {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
              max-width: 100%;
              padding: 0;
              margin: 0;
              display: flex;
              flex-direction: column;
              min-height: calc(297mm - 14mm);
            }
            .ficha-print-lower {
              flex: 1 1 auto;
              display: flex;
              flex-direction: column;
              justify-content: flex-end;
              margin-top: auto;
              padding-top: 6px;
              page-break-inside: avoid;
              break-inside: avoid;
            }
            .ficha-print-orientacoes {
              flex: 1 1 auto;
              justify-content: center;
              padding-bottom: 22px;
            }
            .ficha-print-footer {
              margin-top: 8px;
            }
          }
        `}
      </style>
      <div className="no-print mb-3 mt-2 flex flex-col gap-2.5 lg:flex-row lg:items-end lg:justify-between">
        <div className="min-w-0 flex-1">
          <PageHeader
            compact
            title="Ficha Diária de Produção"
            description="Lançamento diário de sobra, produção e observações por item."
          />
          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-neutral-600 sm:text-sm">
            {statusAssinaturaInformativo && (
              <span
                className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${statusAssinaturaBadgeClass}`}
                role="status"
              >
                {statusAssinaturaInformativo.texto}
              </span>
            )}
            <span>
              Dia da semana:{" "}
              <strong className="capitalize text-neutral-800">
                {diaSemanaLabel}
              </strong>
            </span>
            {podeAbrirFichaTransferencia ? (
              <Link
                to={`/controle-producao/ficha-transferencia?data=${dataFicha}`}
                className="font-semibold text-primary hover:underline"
              >
                Ficha de Transferência
              </Link>
            ) : null}
            <span>
              Segunda-feira da semana:{" "}
              <strong className="text-neutral-800">{segundaSemanaLabel}</strong>
            </span>
            <FichaDiariaContextPanel
              messages={contextMessages}
              autoSaving={autoSaving}
            />
          </div>
        </div>
        <div className="grid w-full grid-cols-1 gap-2 sm:grid-cols-2 lg:flex lg:w-auto lg:shrink-0 lg:gap-3">
          {!ocultarSeletoresLojaData ? (
          <label className="min-w-0 text-sm font-medium text-neutral-700 lg:min-w-[12rem] lg:flex-1">
            Loja
            <ReactSelect<FichaSelectOpt, false>
              inputId="ficha-loja-select"
              options={lojaOptions.map((loja) => ({
                value: loja.id,
                label: loja.nome,
              }))}
              value={
                lojaOptions
                  .map((loja) => ({ value: loja.id, label: loja.nome }))
                  .find((opt) => opt.value === selectedLojaId) ?? null
              }
              onChange={(opt: SingleValue<FichaSelectOpt>) => {
                setSelectedSetorId(undefined);
                setSelectedLojaId(opt?.value);
              }}
              placeholder="Selecionar loja"
              isSearchable
              styles={fichaSelectStyles}
              menuPortalTarget={
                typeof document !== "undefined" ? document.body : null
              }
              menuPosition="fixed"
            />
          </label>
          ) : null}
          {!ocultarSeletoresLojaData ? (
          <label className="min-w-0 text-sm font-medium text-neutral-700 lg:min-w-[10rem] lg:flex-1">
            Data da ficha
            <input
              type="date"
              value={dataFicha}
              onChange={(e) => {
                const raw = e.target.value;
                if (!raw) return;
                setSelectedSetorId(undefined);
                setDataFicha(raw);
              }}
              className="mt-1 block h-10 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm"
            />
          </label>
          ) : null}
          {turnoEhDia &&
            isFichaOperacionalAtual &&
            (payload?.setoresDisponiveis?.length ?? 0) > 0 &&
            !mostrarVisaoGeral && (
            <label className="min-w-0 text-sm font-medium text-neutral-700 sm:col-span-2 lg:col-span-1 lg:min-w-[10rem] lg:flex-1">
              Setor
              <ReactSelect<FichaSelectOpt, false>
                inputId="ficha-setor-select"
                options={(payload?.setoresDisponiveis ?? []).map((setor) => ({
                  value: setor.id,
                  label: setor.nome,
                }))}
                value={
                  (payload?.setoresDisponiveis ?? [])
                    .map((setor) => ({ value: setor.id, label: setor.nome }))
                    .find((opt) => opt.value === selectedSetorId) ?? null
                }
                onChange={(opt: SingleValue<FichaSelectOpt>) => {
                  setSelectedSetorId(opt?.value);
                  if (opt?.value) setGestaoNoGeral(false);
                }}
                placeholder="Selecionar setor"
                isClearable
                isSearchable
                styles={fichaSelectStyles}
                menuPortalTarget={
                  typeof document !== "undefined" ? document.body : null
                }
                menuPosition="fixed"
              />
            </label>
          )}
          {turnoEhDia &&
            !mostrarVisaoGeral &&
            (setorAtual?.id ?? selectedSetorId) != null && (
            <div className="flex items-end sm:col-span-2 lg:col-span-1">
              <BotaoEquipeSetor
                onClick={() => {
                  const id = setorAtual?.id ?? selectedSetorId;
                  if (!id) return;
                  setEquipeSetor({
                    setorId: id,
                    setorNome:
                      setorAtual?.nome ||
                      payload?.setoresDisponiveis?.find((s) => s.id === id)
                        ?.nome ||
                      "",
                  });
                }}
              />
            </div>
          )}
          {podeVerFichaCompleta && !mostrarVisaoGeral && !fichaHistorica ? (
            <div className="flex items-end sm:col-span-2 lg:col-span-1">
              <button
                type="button"
                onClick={() => setModalFichaCompleta(true)}
                className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-primary px-3 text-sm font-semibold text-white touch-manipulation hover:bg-primary-dark lg:w-auto"
              >
                <Radio className="h-4 w-4 shrink-0" aria-hidden />
                Ficha completa ao vivo
              </button>
            </div>
          ) : null}
        </div>
      </div>

      <div
        className="no-print mb-3 flex flex-col items-stretch gap-2.5 sm:flex-row sm:items-center"
        role="status"
        aria-live="polite"
        aria-label={`Turno ${labelTurnoCurto(turnoAtual)}, ${horarioTurnoAtual}`}
      >
        <div className="flex min-w-0 flex-1 items-center gap-2.5">
          <hr className="min-w-0 flex-1 border-neutral-200" />
          <div className="flex shrink-0 flex-col items-center gap-1 sm:flex-row sm:gap-2">
            <span
              className={`rounded-full px-2.5 py-0.5 text-xs font-bold uppercase tracking-wide text-white ${
                turnoEhNoite ? "bg-indigo-700" : "bg-amber-600"
              }`}
            >
              {labelTurnoCurto(turnoAtual)}
            </span>
            <span className="text-xs font-medium text-neutral-600 sm:text-sm">
              {horarioTurnoAtual}
            </span>
          </div>
          <hr className="min-w-0 flex-1 border-neutral-200" />
        </div>
        {podeSimularTurno && !ocultarSeletorTurnoTeste ? (
          <div className="flex shrink-0 items-center justify-center gap-2 rounded-full border border-dashed border-amber-400 bg-amber-50 px-2 py-1">
            <span
              className="text-[10px] font-bold uppercase tracking-wide text-amber-800"
              title="No turno Dia, as assinaturas de setor e produção total ficam disponíveis."
            >
              Teste
            </span>
            <div className="inline-flex rounded-full bg-white p-0.5 shadow-sm">
              <button
                type="button"
                onClick={() => {
                  setTurnoSimulado("dia");
                  gravarTurnoSimuladoStorage("dia");
                  setGestaoNoGeral(!fichaHistorica);
                }}
                className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase ${
                  turnoSimulado === "dia"
                    ? "bg-amber-600 text-white"
                    : "text-neutral-600 hover:bg-neutral-100"
                }`}
              >
                Dia
              </button>
              <button
                type="button"
                onClick={() => {
                  setTurnoSimulado("noite");
                  gravarTurnoSimuladoStorage("noite");
                  setGestaoNoGeral(false);
                  setSelectedSetorId(undefined);
                }}
                className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase ${
                  turnoSimulado === "noite"
                    ? "bg-indigo-700 text-white"
                    : "text-neutral-600 hover:bg-neutral-100"
                }`}
              >
                Noite
              </button>
            </div>
          </div>
        ) : null}
        <p className="sr-only">
          {labelTurnoComHorarios(turnoAtual, payload?.horariosTurno)}
        </p>
      </div>

      {loading ? (
        <div
          className="no-print flex min-h-[16rem] flex-col items-center justify-center gap-3 py-12"
          role="status"
          aria-live="polite"
          aria-label="Carregando ficha diária"
        >
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm font-medium text-neutral-600">
            Carregando ficha diária…
          </p>
        </div>
      ) : null}

      {!loading && mostrarVisaoGeral && (
        <FichaDiariaResumoGestao
          setores={(payload?.resumoSetores ?? []).map((s) => ({
            ...s,
            producaoImagem:
              payload?.assinaturasSetores?.find((a) => a.setorId === s.setorId)
                ?.imagemAssinatura ?? null,
          }))}
          conferenciaNome={ficha?.userConfereNome}
          conferenciaData={ficha?.dataAssinaturaConf}
          conferenciaImagem={ficha?.imagemAssinaturaConf}
          modoTeste={false}
          modoNoite={turnoEhNoite}
          producaoTotalAssinada={ficha?.assinadoProducao === true}
          producaoTotalNome={ficha?.userProducaoNome}
          producaoTotalData={ficha?.dataAssinaturaProducao}
          producaoTotalImagem={ficha?.imagemAssinaturaProducao}
          podeAssinarProducaoTotal={podeAssinarProducaoTotal}
          assinandoProducaoTotal={assinandoProducaoTotal}
          mostrarAssinaturaProducaoTotal={
            podeAssinarProducaoTotal || ficha?.assinadoProducao === true
          }
          onSelectSetor={(setorId) => {
            setSelectedSetorId(setorId);
            setGestaoNoGeral(false);
          }}
          onAbrirFichaUnificada={() => setGestaoNoGeral(false)}
          onAbrirFichaCompleta={
            podeVerFichaCompleta && !fichaHistorica
              ? () => setModalFichaCompleta(true)
              : undefined
          }
          onAssinarProducaoTotal={() => setModalAssinatura("producao_total")}
        />
      )}

      {!loading && exigeSetor && !temResumoGestao && (
        <div className="no-print mt-4 overflow-hidden rounded-xl border border-neutral-200/90 bg-white p-4 shadow-sm ring-1 ring-black/[0.03]">
          <div className="flex items-start gap-3">
            <span
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10"
              aria-hidden
            >
              <Layers className="h-4 w-4 text-primary" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-neutral-800">
                Escolha o setor da ficha DIA
              </p>
              <p className="mt-0.5 text-xs text-neutral-500">
                No dia você vê só os itens do seu setor. A conferência da NOITE
                continua com todos.
              </p>
              {(payload?.setoresDisponiveis?.length ?? 0) === 0 ? (
                <p className="mt-3 text-sm text-neutral-600">
                  Você não tem setor vinculado nesta loja. Peça ao administrador
                  para vincular seu usuário.
                </p>
              ) : (
                <div className="mt-3 flex flex-wrap gap-2">
                  {payload?.setoresDisponiveis?.map((setor) => (
                    <div key={setor.id} className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => setSelectedSetorId(setor.id)}
                        className="inline-flex min-h-10 items-center rounded-full border border-primary/20 bg-primary/5 px-4 text-sm font-semibold text-primary transition-all duration-200 ease-out hover:-translate-y-0.5 hover:border-primary hover:bg-primary hover:text-white hover:shadow-[0_10px_16px_-6px_rgba(147,32,24,0.45)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary touch-manipulation"
                      >
                        {setor.nome}
                      </button>
                      <BotaoEquipeSetor
                        compact
                        onClick={() =>
                          setEquipeSetor({
                            setorId: setor.id,
                            setorNome: setor.nome,
                          })
                        }
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {!loading && exibirTabelaFicha && !mostrarVisaoGeral && (
        <>
          {temResumoGestao && !fichaHistorica ? (
            <div className="no-print mb-4 flex flex-col gap-2 sm:flex-row sm:items-center">
              <button
                type="button"
                onClick={() => setGestaoNoGeral(true)}
                className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl border border-neutral-300 bg-white px-4 text-sm font-semibold text-neutral-700 touch-manipulation sm:w-auto sm:justify-start"
              >
                <ArrowLeft className="h-4 w-4 shrink-0" />
                Voltar à visão geral
                {turnoEhDia && setorAtual?.nome ? (
                  <span className="font-medium text-neutral-400">
                    · {setorAtual.nome}
                  </span>
                ) : null}
              </button>
              {turnoEhDia && setorAtual ? (
                <BotaoEquipeSetor
                  onClick={() =>
                    setEquipeSetor({
                      setorId: setorAtual.id,
                      setorNome: setorAtual.nome,
                    })
                  }
                />
              ) : null}
            </div>
          ) : null}
          <div className="no-print mb-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch">
              <label className="min-w-0 flex-1">
                <span className="sr-only">Buscar item</span>
                <div className="relative">
                  <Search
                    className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400"
                    aria-hidden
                  />
                  <input
                    type="search"
                    value={buscaItem}
                    onChange={(e) => setBuscaItem(e.target.value)}
                    placeholder="Buscar item…"
                    className="h-11 w-full rounded-xl border border-neutral-300 py-2 pl-9 pr-3 text-sm"
                    autoComplete="off"
                  />
                </div>
              </label>
              {layoutFichaUnificada ? (
                <button
                  type="button"
                  onClick={() => setDataFicha(dataProducaoAnterior)}
                  className="inline-flex h-11 shrink-0 items-center justify-center gap-2 rounded-xl border border-neutral-300 bg-white px-4 text-sm font-semibold text-neutral-700 touch-manipulation transition-colors hover:bg-neutral-50"
                >
                  <Eye className="h-4 w-4 shrink-0" aria-hidden />
                  Ver produção do dia
                </button>
              ) : null}
              {turnoEhNoite && vendoProducaoDoDiaAnterior ? (
                <button
                  type="button"
                  onClick={() => setDataFicha(dataFichaOperacional)}
                  className="inline-flex h-11 shrink-0 items-center justify-center gap-2 rounded-xl border border-indigo-300 bg-indigo-50 px-4 text-sm font-semibold text-indigo-800 touch-manipulation transition-colors hover:bg-indigo-100"
                >
                  <ArrowLeft className="h-4 w-4 shrink-0" aria-hidden />
                  Voltar à conferência
                </button>
              ) : null}
            </div>
            {buscaItem.trim() ? (
              <p className="mt-1 text-xs text-neutral-500">
                {itensFiltrados.length} de {payload?.itens?.length ?? 0} itens
              </p>
            ) : null}
          </div>

          {!podeEditarProducao && payload?.motivoBloqueioProducao && turnoEhDia && (
            <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-800">
              EDIÇÃO BLOQUEADA: {payload.motivoBloqueioProducao}
            </div>
          )}

          {/* Mobile: cards sem rolagem horizontal */}
          <div className="no-print space-y-3 xl:hidden">
            {blocosFicha.map((bloco) => (
              <div key={`setor-m-${bloco.setorId ?? "unico"}`} className="space-y-3">
                {bloco.setorNome ? (
                  <div className="flex items-center justify-between gap-2 rounded-lg bg-[#fff5f5] px-3 py-2 text-xs font-bold uppercase tracking-wide text-primary ring-1 ring-primary/15">
                    <span>{bloco.setorNome}</span>
                    {bloco.setorId != null ? (
                      <BotaoEquipeSetor
                        compact
                        onClick={() =>
                          setEquipeSetor({
                            setorId: bloco.setorId!,
                            setorNome: bloco.setorNome || "",
                          })
                        }
                      />
                    ) : null}
                  </div>
                ) : null}
            {bloco.itens.filter((i) => (i.indexNoGrupo ?? 0) === 0).map((item) => {
              const linhaKey = item.lojaIdOutra ? item.itemIdOutraLoja! : item.itemId;
              const overlaySetor = bloco.setorId != null;
              const local = linhaVisualSetor(
                item,
                linhas[linhaKey],
                overlaySetor
              );
              const estadoProducao = estadoLinhaProducao(
                local,
                producaoTravadaPorAssinatura
              );
              const foraDaUnidade = local?.naoProduzidoPelaUnidade === true;
              const isLinhaOutraLoja = !!item.lojaIdOutra;
              const isentaPorSugestaoZero = linhaIsentaCronometroPorSugestaoZero(
                item,
                local,
                unds,
                dataFicha
              );
              const bloqueioLancamento = bloqueioLancamentoProducaoLinha({
                turnoDia: podeEditarProducao,
                foraDaUnidade,
                jaProduziu: local?.producaoChecada === true,
                cronometragem: overlaySetor
                  ? item.cronometragem
                  : cronometragemDoGrupo(item, itensFiltrados),
                nowMs: nowTick,
                isentaPorSugestaoZero,
              });
              const podeEditarProducaoItem =
                podeEditarProducao && !foraDaUnidade && !bloqueioLancamento;
              const inputClass = (editavel: boolean) =>
                editavel
                  ? "w-full min-h-11 min-w-0 rounded-lg border border-neutral-300 px-3 py-2 text-base"
                  : `${inputDesabilitadoClass} min-h-11 px-3 py-2 text-base`;
              const linhasGrupo = (item.totalLinhasGrupo ?? 1) > 1
                ? itensFiltrados.filter((i) => i.itemId === item.itemId)
                : null;
              const unidadesLinha = linhasGrupo ?? [item];
              const temGrupo = Boolean(linhasGrupo);
              const ocultaCronometroPorSugestao =
                grupoIsentoCronometroPorSugestaoZero(
                  unidadesLinha,
                  linhas,
                  unds,
                  overlaySetor,
                  dataFicha
                );
              const inputClassCompact = (editavel: boolean) =>
                editavel
                  ? "w-full min-h-8 min-w-0 rounded-md border border-neutral-300 px-1.5 py-1 text-sm"
                  : `${inputDesabilitadoClass} min-h-8 px-1.5 py-1 text-sm`;
              const obsSobraPreenchida = unidadesLinha.some((lg) => {
                const k = lg.lojaIdOutra ? lg.itemIdOutraLoja! : lg.itemId;
                return Boolean((linhas[k]?.observacaoSobra ?? "").trim());
              });
              const obsProducaoPreenchida = unidadesLinha.some((lg) => {
                const k = lg.lojaIdOutra ? lg.itemIdOutraLoja! : lg.itemId;
                return Boolean((linhas[k]?.observacaoProducao ?? "").trim());
              });
              const obsProducaoAlerta =
                mostraProduziu &&
                podeEditarProducao &&
                unidadesLinha.some((lg) => {
                  const k = lg.lojaIdOutra ? lg.itemIdOutraLoja! : lg.itemId;
                  const loc = linhas[k];
                  return (
                    loc?.producaoChecada !== true &&
                    loc?.naoProduzidoPelaUnidade !== true
                  );
                });
              return (
                <article
                  key={`${item.itemId}-mobile-${bloco.setorId ?? "x"}`}
                  className={`rounded-xl border p-3 shadow-sm ${
                    foraDaUnidade
                      ? "border-slate-300 bg-slate-50"
                      : "border-neutral-200 bg-white"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="min-w-0 flex-1">
                      <h3 className="break-words text-sm font-semibold leading-snug text-neutral-900">
                        {item.descricao}
                      </h3>
                      <p className="mt-0.5 break-words text-[10px] font-medium leading-tight text-neutral-400">
                        {item.pesoPadrao}
                      </p>
                    </div>
                  </div>
                  {mostraCronometro &&
                    !isLinhaOutraLoja &&
                    !ocultaCronometroPorSugestao && (
                    <div className="mt-1.5">
                      {foraDaUnidade ? (
                        <p className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-700">
                          Cronômetro não se aplica — item não produzido pela
                          unidade.
                        </p>
                      ) : (
                        <CronometroItemButtons
                          variant="touch"
                          cronometragem={cronometragemComTick(
                            item.cronometragem,
                            nowTick
                          )}
                          disabled={!podeEditarProducao}
                          somenteLeitura={cronometroSomenteLeitura}
                          onIniciar={() =>
                            void acionarCronometroItem(
                              item.itemId,
                              "iniciar",
                              item.descricao
                            )
                          }
                          onPausar={() =>
                            void acionarCronometroItem(
                              item.itemId,
                              "pausar",
                              item.descricao
                            )
                          }
                          onRetomar={() =>
                            void acionarCronometroItem(
                              item.itemId,
                              "retomar",
                              item.descricao
                            )
                          }
                        />
                      )}
                    </div>
                  )}
                  {mostrarCamposBaseSobra ? (
                  <div
                    className={
                      !layoutFichaUnificada && !temGrupo
                        ? "mt-2 grid grid-cols-2 gap-1.5"
                        : "mt-2 space-y-1.5"
                    }
                  >
                    <div className="min-w-0">
                      <span className="mb-0.5 block text-[10px] font-bold uppercase tracking-wide text-neutral-800">
                        Base
                      </span>
                      {podeEditarBase ? (
                        <div className={!layoutFichaUnificada && !temGrupo ? gridUndCompactClass : gridUndCardClass}>
                          <input
                            value={local?.baseValor || ""}
                            onChange={(e) =>
                              atualizarLinha(item.itemId, {
                                baseValor: e.target.value,
                              })
                            }
                            className={`min-w-0 ${!layoutFichaUnificada && !temGrupo ? inputClassCompact(true) : inputClass(true)}`}
                            inputMode="decimal"
                          />
                          <UndMedidaSelect
                            unds={unds}
                            value={undFichaDoItem(item, local?.undMedidaIdBase)}
                            onChange={(undMedidaIdBase) =>
                              atualizarLinha(
                                item.itemId,
                                { undMedidaIdBase },
                                { salvarAgora: true }
                              )
                            }
                            disabled={!podeEditarBase}
                            className={!layoutFichaUnificada && !temGrupo ? undSelectCompactClass : undSelectCardClass}
                            compact
                          />
                        </div>
                      ) : temGrupo ? (
                        <div className="grid grid-cols-2 gap-1.5">
                          {unidadesLinha.map((lg) => (
                            <div
                              key={`${lg.itemId}-${lg.lojaIdOutra ?? "local"}`}
                              className="min-w-0"
                            >
                              <span className={pillUnidadeClass(!!lg.lojaIdOutra)}>
                                {labelUnidadeFicha(lg)}
                              </span>
                              <input
                                type="text"
                                disabled
                                value={lg.base || ""}
                                className={`${inputDesabilitadoClass} mt-0.5 min-h-8 w-full px-1.5 py-1 text-sm`}
                                tabIndex={-1}
                              />
                            </div>
                          ))}
                        </div>
                      ) : (
                        <input
                          type="text"
                          disabled
                          value={item.base || ""}
                          className={`${inputDesabilitadoClass} min-h-8 px-1.5 py-1 text-sm`}
                          tabIndex={-1}
                        />
                      )}
                    </div>
                    {!layoutFichaUnificada && (
                      <div
                        className={
                          temGrupo ? "grid grid-cols-2 gap-1.5" : "min-w-0"
                        }
                      >
                        {temGrupo ? (
                          <span className="col-span-2 text-[10px] font-bold uppercase tracking-wide text-neutral-800">
                            Sobra
                          </span>
                        ) : null}
                        {unidadesLinha.map((lg) => {
                      const lgKey = lg.lojaIdOutra ? lg.itemIdOutraLoja! : lg.itemId;
                      const lgLocal = linhas[lgKey];
                      const lgLabel = labelUnidadeFicha(lg);
                      return (
                        <div key={`sobra-${lgKey}`} className="min-w-0">
                          <label className="mb-0.5 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-neutral-800">
                            {temGrupo ? (
                              <span className={pillUnidadeClass(!!lg.lojaIdOutra)}>
                                {lgLabel}
                              </span>
                            ) : (
                              "Sobra"
                            )}
                          </label>
                          {podeEditarSobra ? (
                            <div className={gridUndCompactClass}>
                              <input
                                value={sobraExibicaoLinha(
                                  lg,
                                  lgLocal,
                                  podeEditarSobra
                                )}
                                onChange={(e) =>
                                  atualizarLinha(lgKey, { sobra: e.target.value })
                                }
                                className={`min-w-0 ${inputClassCompact(true)}`}
                                inputMode="decimal"
                              />
                              <UndMedidaSelect
                                unds={unds}
                                value={undSobraExibicaoLinha(lg, lgLocal, podeEditarSobra)}
                                onChange={(undMedidaIdSobra) =>
                                  atualizarLinha(lgKey, { undMedidaIdSobra }, { salvarAgora: true })
                                }
                                disabled={!podeEditarSobra || !podeAlterarUnd}
                                className={undSelectCompactClass}
                            compact
                              />
                            </div>
                          ) : (
                            <div className={gridUndCompactClass}>
                              <input type="text" disabled value={sobraExibicaoLinha(lg, lgLocal, podeEditarSobra)} className={`min-w-0 ${inputClassCompact(false)}`} tabIndex={-1} />
                              <UndMedidaSelect
                                unds={unds}
                                value={undSobraExibicaoLinha(lg, lgLocal, podeEditarSobra)}
                                onChange={() => {}}
                                disabled
                                compact
                                className={undSelectCompactClass}
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                      </div>
                    )}
                    {!layoutFichaUnificada ? (
                      <div className={!temGrupo ? "col-span-2" : undefined}>
                        <AccordionMobileFicha
                          titulo="Observações da sobra"
                          preenchido={obsSobraPreenchida}
                        >
                    {unidadesLinha.map((lg) => {
                      const lgKey = lg.lojaIdOutra ? lg.itemIdOutraLoja! : lg.itemId;
                      const lgLocal = linhas[lgKey];
                      const lgLabel = labelUnidadeFicha(lg);
                      return (
                        <button
                          key={`obs-sobra-${lgKey}`}
                          type="button"
                          onClick={() =>
                            abrirModalObservacao(lgKey, item.descricao, "sobra")
                          }
                          title={lgLocal?.observacaoSobra?.trim() || undefined}
                          className={`flex w-full flex-col gap-1 text-left ${classBotaoObservacaoSobra(
                            lgLocal,
                            podeEditarSobra,
                            "card",
                          )}`}
                        >
                          <span className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wide text-neutral-500">
                            Obs. sobra
                            {temGrupo && (
                              <span className={pillUnidadeClass(!!lg.lojaIdOutra)}>
                                {lgLabel}
                              </span>
                            )}
                          </span>
                          <span className="block whitespace-pre-wrap break-words text-sm leading-snug">
                            {lgLocal?.observacaoSobra?.trim() ||
                              (podeEditarSobra
                                ? "Toque para observação da sobra"
                                : "—")}
                          </span>
                        </button>
                      );
                    })}
                        </AccordionMobileFicha>
                      </div>
                    ) : null}
                  </div>
                  ) : null}
                    {mostraLancamentoProducao ? (
                    <div className="mt-2.5 space-y-2.5 rounded-xl border border-neutral-200 bg-neutral-50/60 p-2.5">
                    {unidadesLinha.map(
                      (lg) => {
                    const lgKey = lg.lojaIdOutra ? lg.itemIdOutraLoja! : lg.itemId;
                    const lgLocal = linhaVisualSetor(
                      lg,
                      linhas[lgKey],
                      overlaySetor && !lg.lojaIdOutra
                    );
                    const lgLabel = (lg.unidadeLabel ?? "").replace("Dom Belloni - ", "");
                    const lgIsOutra = !!lg.lojaIdOutra;
                    const lgForaDaUnidade = lgLocal?.naoProduzidoPelaUnidade === true;
                    const lgBloqueio = bloqueioLancamentoProducaoLinha({
                      turnoDia: podeEditarProducao,
                      foraDaUnidade: lgForaDaUnidade,
                      jaProduziu: lgLocal?.producaoChecada === true,
                      cronometragem: overlaySetor && !lg.lojaIdOutra
                        ? lg.cronometragem
                        : cronometragemDoGrupo(
                            lg,
                            itensFiltrados
                          ),
                      nowMs: nowTick,
                      isentaPorSugestaoZero:
                        linhaIsentaCronometroPorSugestaoZero(
                          lg,
                          lgLocal,
                          unds,
                          dataFicha
                        ),
                    });
                    const lgPodeEditar = podeEditarProducao && !lgForaDaUnidade && !lgBloqueio;
                    const lgEstado = estadoLinhaProducao(lgLocal, producaoTravadaPorAssinatura);
                    return (
                      <div key={`prod-${lgKey}`} className="min-w-0">
                        <label className="mb-1 flex items-center gap-2 text-[10px] font-bold uppercase tracking-wide text-neutral-800">
                          Produção
                          {temGrupo && (
                            <span className={pillUnidadeClass(lgIsOutra)}>{lgLabel}</span>
                          )}
                        </label>
                        <div className={gridUndCardClass}>
                          <input
                            disabled={!lgPodeEditar}
                            value={lgForaDaUnidade ? "" : lgLocal?.producao || ""}
                            placeholder={
                              lgForaDaUnidade
                                ? "—"
                                : podeEditarProducao && !(lgLocal?.producao ?? "").trim()
                                  ? sugestaoProducaoLinha(lg, lgLocal, unds) || "—"
                                  : undefined
                            }
                            onChange={(e) => atualizarLinha(lgKey, { producao: e.target.value })}
                            className={`min-w-0 ${classProducaoInput(lgPodeEditar, lgLocal, producaoTravadaPorAssinatura)}`}
                            inputMode="decimal"
                            tabIndex={lgPodeEditar ? 0 : -1}
                          />
                          <UndMedidaSelect
                            unds={unds}
                            value={undFichaDoItem(lg, lgLocal?.undMedidaIdProducao)}
                            onChange={(undMedidaIdProducao) =>
                              atualizarLinha(lgKey, { undMedidaIdProducao }, { salvarAgora: true })
                            }
                            disabled={!lgPodeEditar || !podeAlterarUnd}
                            className={undSelectCardClass}
                            compact
                          />
                        </div>
                        {lgBloqueio && (
                          <p className="mt-1 text-xs font-medium text-amber-700">{lgBloqueio}</p>
                        )}
                        {mostraProduziu && (
                        <div className="mt-1.5 flex items-center gap-3">
                          <ProducaoToggle
                            checked={lgLocal?.producaoChecada === true}
                            estado={lgEstado}
                            disabled={!podeEditarProducao || lgForaDaUnidade || Boolean(lgBloqueio)}
                            layout="stacked"
                            itemDescricao={`${item.descricao}${lgLabel ? ` (${lgLabel})` : ""}`}
                            onChange={(checada) =>
                              void handleToggleProduzido(lgKey, item.descricao, checada)
                            }
                          />
                          {podeEditarProducao && !lgForaDaUnidade && !(lgLocal?.producaoChecada) && (
                            <button
                              type="button"
                              className={`shrink-0 rounded px-2 py-1 text-xs font-semibold ${
                                lgLocal?.naoProduzidoPelaUnidade
                                  ? "bg-red-100 text-red-700 ring-1 ring-red-300"
                                  : "bg-neutral-100 text-neutral-600 hover:bg-red-50 hover:text-red-700"
                              }`}
                              onClick={() => void handleNaoProduziu(lgKey, `${item.descricao}${lgLabel ? ` (${lgLabel})` : ""}`)}
                            >
                              {lgLocal?.naoProduzidoPelaUnidade ? "Não produziu ✓" : "Não produziu"}
                            </button>
                          )}
                        </div>
                        )}
                      </div>
                    );
                  })}
                      <AccordionMobileFicha
                        titulo="Observações da produção"
                        preenchido={obsProducaoPreenchida}
                        alerta={obsProducaoAlerta}
                      >
                    {unidadesLinha.map((lg) => {
                        const lgKey = lg.lojaIdOutra
                          ? lg.itemIdOutraLoja!
                          : lg.itemId;
                        const lgLocal = linhas[lgKey];
                        const lgLabel = labelUnidadeFicha(lg);
                        return (
                          <button
                            key={`obs-prod-${lgKey}`}
                            type="button"
                            onClick={() =>
                              abrirModalObservacao(
                                lgKey,
                                item.descricao,
                                "producao",
                                mostraProduziu &&
                                  !lgLocal?.producaoChecada &&
                                  lgLocal?.naoProduzidoPelaUnidade !== true &&
                                  podeEditarProducao
                                  ? { justificativaNaoProduzido: true }
                                  : undefined,
                              )
                            }
                            title={
                              lgLocal?.observacaoProducao?.trim() || undefined
                            }
                            className={`flex w-full flex-col gap-1 text-left ${classBotaoObservacaoProducao(
                              lgLocal,
                              podeEditarProducao,
                              "card",
                            )}`}
                          >
                            <span className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wide text-neutral-500">
                              {mostraProduziu &&
                              podeEditarProducao &&
                              lgLocal?.producaoChecada !== true
                                ? "Justificativa / Obs. produção"
                                : "Obs. produção"}
                              {temGrupo && (
                                <span className={pillUnidadeClass(!!lg.lojaIdOutra)}>
                                  {lgLabel}
                                </span>
                              )}
                            </span>
                            <span className="block whitespace-pre-wrap break-words text-sm leading-snug">
                              {lgLocal?.observacaoProducao?.trim() ||
                                (mostraProduziu &&
                                podeEditarProducao &&
                                lgLocal?.producaoChecada !== true
                                  ? "Obrigatória se não produziu"
                                  : "Toque para ver ou digitar")}
                            </span>
                          </button>
                        );
                      })}
                      </AccordionMobileFicha>
                      </div>
                    ) : null}
                    {layoutFichaUnificada ? (
                      <div className="mt-2 space-y-1.5">
                        <div
                          className={
                            temGrupo ? "grid grid-cols-2 gap-1.5" : "min-w-0"
                          }
                        >
                          {temGrupo ? (
                            <span className="col-span-2 text-[10px] font-bold uppercase tracking-wide text-neutral-800">
                              Sobra
                            </span>
                          ) : null}
                          {unidadesLinha.map((lg) => {
                        const lgKey = lg.lojaIdOutra
                          ? lg.itemIdOutraLoja!
                          : lg.itemId;
                        const lgLocal = linhas[lgKey];
                        const lgLabel = labelUnidadeFicha(lg);
                        return (
                          <div key={`sobra-noite-${lgKey}`} className="min-w-0">
                            <label className="mb-0.5 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-neutral-800">
                              {temGrupo ? (
                                <span className={pillUnidadeClass(!!lg.lojaIdOutra)}>
                                  {lgLabel}
                                </span>
                              ) : (
                                "Sobra"
                              )}
                            </label>
                            {podeEditarSobra ? (
                              <div className={gridUndCompactClass}>
                                <input
                                  value={sobraExibicaoLinha(
                                    lg,
                                    lgLocal,
                                    podeEditarSobra
                                  )}
                                  onChange={(e) =>
                                    atualizarLinha(lgKey, { sobra: e.target.value })
                                  }
                                  className={`min-w-0 ${inputClassCompact(true)}`}
                                  inputMode="decimal"
                                />
                                <UndMedidaSelect
                                  unds={unds}
                                  value={undSobraExibicaoLinha(lg, lgLocal, podeEditarSobra)}
                                  onChange={(undMedidaIdSobra) =>
                                    atualizarLinha(
                                      lgKey,
                                      { undMedidaIdSobra },
                                      { salvarAgora: true },
                                    )
                                  }
                                  disabled={!podeEditarSobra || !podeAlterarUnd}
                                  className={undSelectCompactClass}
                            compact
                                />
                              </div>
                            ) : (
                              <div className={gridUndCompactClass}>
                                <input
                                  type="text"
                                  disabled
                                  value={sobraExibicaoLinha(lg, lgLocal, podeEditarSobra)}
                                  className={`min-w-0 ${inputClassCompact(false)}`}
                                  tabIndex={-1}
                                />
                                <UndMedidaSelect
                                  unds={unds}
                                  value={undSobraExibicaoLinha(lg, lgLocal, podeEditarSobra)}
                                  onChange={() => {}}
                                  disabled
                                  className={undSelectCompactClass}
                            compact
                                />
                              </div>
                            )}
                          </div>
                        );
                      })}
                        </div>
                        <AccordionMobileFicha
                          titulo="Observações da sobra"
                          preenchido={obsSobraPreenchida}
                        >
                      {unidadesLinha.map((lg) => {
                        const lgKey = lg.lojaIdOutra
                          ? lg.itemIdOutraLoja!
                          : lg.itemId;
                        const lgLocal = linhas[lgKey];
                        const lgLabel = labelUnidadeFicha(lg);
                        return (
                          <button
                            key={`obs-sobra-noite-${lgKey}`}
                            type="button"
                            onClick={() =>
                              abrirModalObservacao(lgKey, item.descricao, "sobra")
                            }
                            title={lgLocal?.observacaoSobra?.trim() || undefined}
                            className={`flex w-full flex-col gap-1 text-left ${classBotaoObservacaoSobra(
                              lgLocal,
                              podeEditarSobra,
                              "card",
                            )}`}
                          >
                            <span className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wide text-neutral-500">
                              Obs. sobra
                              {temGrupo && (
                                <span className={pillUnidadeClass(!!lg.lojaIdOutra)}>
                                  {lgLabel}
                                </span>
                              )}
                            </span>
                            <span className="block whitespace-pre-wrap break-words text-sm leading-snug">
                              {lgLocal?.observacaoSobra?.trim() ||
                                (podeEditarSobra
                                  ? "Toque para observação da sobra"
                                  : "—")}
                            </span>
                          </button>
                        );
                      })}
                        </AccordionMobileFicha>
                      </div>
                    ) : null}
                </article>
              );
            })}
              </div>
            ))}
          </div>

          {/* Desktop: tabela editável */}
          <div className="no-print hidden w-full overflow-hidden rounded-lg border border-neutral-200 bg-white xl:block">
            <table className="w-full table-fixed border-collapse text-sm">
              <thead className="bg-neutral-50">
                <tr className="border-b border-neutral-200">
                  <th className={`w-[16%] ${cabecalhoTabelaClass}`}>
                    Item
                  </th>
                  {mostrarCamposBaseSobra ? (
                  <th className={`w-[15%] ${cabecalhoTabelaClass}`}>
                    Base
                  </th>
                  ) : null}
                  {layoutFichaUnificada ? (
                    <>
                      {mostraLancamentoProducao && (
                        <th className={`w-[16%] ${cabecalhoTabelaClass}`}>
                          Produções
                        </th>
                      )}
                      <th className={`w-[16%] ${cabecalhoTabelaClass}`}>
                        Sobras
                      </th>
                    </>
                  ) : (
                    <>
                      {mostrarCamposBaseSobra ? (
                      <th className={`w-[16%] ${cabecalhoTabelaClass}`}>
                        Sobras
                      </th>
                      ) : null}
                      {mostraLancamentoProducao && (
                        <th className={`w-[16%] ${cabecalhoTabelaClass}`}>
                          Produções
                        </th>
                      )}
                      {mostraProduziu && (
                        <th className={`w-[10%] ${cabecalhoTabelaClass} text-center`}>
                          Produziu
                        </th>
                      )}
                    </>
                  )}
                  {mostraCronometro && (
                    <th className={`w-[13%] ${cabecalhoTabelaClass} text-center`}>
                      Tempo
                    </th>
                  )}
                  {layoutFichaUnificada ? (
                    <>
                      {mostraLancamentoProducao ? (
                        <th className={`w-[14%] ${cabecalhoTabelaClass}`}>
                          Obs. Produção
                        </th>
                      ) : null}
                      <th className={`w-[14%] ${cabecalhoTabelaClass}`}>
                        Obs. Sobra
                      </th>
                    </>
                  ) : (
                    <th className={`w-[14%] ${cabecalhoTabelaClass}`}>
                      Obs.
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {blocosFicha.map((bloco) => (
                  <Fragment key={`setor-d-${bloco.setorId ?? "unico"}`}>
                    {bloco.setorNome ? (
                      <tr>
                        <td
                          colSpan={colunasTabelaFicha}
                          className="bg-[#fff5f5] px-3 py-2 text-xs font-bold uppercase tracking-wide text-primary"
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span>{bloco.setorNome}</span>
                            {bloco.setorId != null ? (
                              <BotaoEquipeSetor
                                compact
                                onClick={() =>
                                  setEquipeSetor({
                                    setorId: bloco.setorId!,
                                    setorNome: bloco.setorNome || "",
                                  })
                                }
                              />
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ) : null}
                {bloco.itens.map((item) => {
                  const linhaKey = item.lojaIdOutra ? item.itemIdOutraLoja! : item.itemId;
                  const overlaySetor = bloco.setorId != null;
                  const local = linhaVisualSetor(
                    item,
                    linhas[linhaKey],
                    overlaySetor
                  );
                  const estadoProducao = estadoLinhaProducao(
                    local,
                    producaoTravadaPorAssinatura
                  );
                  const foraDaUnidade = local?.naoProduzidoPelaUnidade === true;
                  const isentaPorSugestaoZero =
                    linhaIsentaCronometroPorSugestaoZero(
                      item,
                      local,
                      unds,
                      dataFicha
                    );
                  const ocultaCronometroPorSugestao =
                    mostraCronometro &&
                    (item.indexNoGrupo ?? 0) === 0 &&
                    grupoIsentoCronometroPorSugestaoZero(
                      (item.totalLinhasGrupo ?? 1) > 1
                        ? bloco.itens.filter((i) => i.itemId === item.itemId)
                        : [item],
                      linhas,
                      unds,
                      overlaySetor,
                      dataFicha
                    );
                  const bloqueioLancamento = bloqueioLancamentoProducaoLinha({
                    turnoDia: podeEditarProducao,
                    foraDaUnidade,
                    jaProduziu: local?.producaoChecada === true,
                    cronometragem: overlaySetor
                      ? item.cronometragem
                      : cronometragemDoGrupo(item, bloco.itens),
                    nowMs: nowTick,
                    isentaPorSugestaoZero,
                  });
                  const podeEditarProducaoItem =
                    podeEditarProducao && !foraDaUnidade && !bloqueioLancamento;
                  return (
                    <tr
                      key={`${item.itemId}-${item.lojaIdOutra ?? "local"}-${bloco.setorId ?? "x"}`}
                      className="border-t border-neutral-200 transition-colors duration-200 hover:bg-neutral-50/90"
                    >
                      <td className={celulaTabelaClass}>
                        <p className="break-words font-medium leading-snug text-neutral-900">
                          {item.descricao}
                          {item.unidadeLabel && (
                            <span className={`ml-1.5 inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                              item.lojaIdOutra
                                ? "bg-sky-100 text-sky-800"
                                : "bg-emerald-100 text-emerald-800"
                            }`}>
                              {item.unidadeLabel.replace("Dom Belloni - ", "")}
                            </span>
                          )}
                        </p>
                        <p className="mt-0.5 break-words text-xs text-neutral-500">
                          {item.pesoPadrao}
                        </p>
                      </td>
                      {mostrarCamposBaseSobra ? (
                      <td className={celulaTabelaClass}>
                        {podeEditarBase ? (
                          <CampoQtyTabela
                            value={local?.baseValor || ""}
                            onChange={(baseValor) =>
                              atualizarLinha(linhaKey, { baseValor })
                            }
                            unds={unds}
                            undId={undFichaDoItem(item, local?.undMedidaIdBase)}
                            onUndChange={(undMedidaIdBase) =>
                              atualizarLinha(
                                linhaKey,
                                { undMedidaIdBase },
                                { salvarAgora: true }
                              )
                            }
                            podeAlterarUnd={podeAlterarUnd}
                            className={inputBaseTabelaClass}
                          />
                        ) : (
                          <input
                            type="text"
                            disabled
                            value={item.base || ""}
                            className={inputDesabilitadoClass}
                            tabIndex={-1}
                          />
                        )}
                      </td>
                      ) : null}
                      {layoutFichaUnificada ? (
                        <>
                          {mostraLancamentoProducao && (
                      <td
                        className={celulaTabelaClass}
                        title={bloqueioLancamento ?? undefined}
                      >
                        <div className="min-w-0">
                          <CampoQtyTabela
                          value={foraDaUnidade ? "" : local?.producao || ""}
                          onChange={(producao) =>
                            atualizarLinha(linhaKey, { producao })
                          }
                          disabled={!podeEditarProducaoItem}
                          unds={unds}
                          undId={undFichaDoItem(
                            item,
                            local?.undMedidaIdProducao
                          )}
                          onUndChange={(undMedidaIdProducao) =>
                            atualizarLinha(
                              linhaKey,
                              { undMedidaIdProducao },
                              { salvarAgora: true }
                            )
                          }
                          podeAlterarUnd={podeAlterarUnd}
                          placeholder={
                            foraDaUnidade
                              ? "—"
                              : podeEditarProducao &&
                                  !(local?.producao ?? "").trim()
                                ? sugestaoProducaoLinha(item, local, unds) ||
                                  "—"
                                : undefined
                          }
                          className={classProducaoInput(
                            podeEditarProducaoItem,
                            local,
                            producaoTravadaPorAssinatura,
                            "table"
                          )}
                          tabIndex={podeEditarProducaoItem ? 0 : -1}
                        />
                          {bloqueioLancamento ? (
                            <p className="mt-1 text-[10px] font-medium leading-snug text-amber-700">
                              {bloqueioLancamento}
                            </p>
                          ) : null}
                        </div>
                      </td>
                          )}
                      <td className={celulaTabelaClass}>
                        <CampoQtyTabela
                          value={sobraExibicaoLinha(item, local, podeEditarSobra)}
                          onChange={(sobra) =>
                            atualizarLinha(linhaKey, { sobra })
                          }
                          disabled={!podeEditarSobra}
                          unds={unds}
                          undId={undSobraExibicaoLinha(item, local, podeEditarSobra)}
                          onUndChange={(undMedidaIdSobra) =>
                            atualizarLinha(
                              linhaKey,
                              { undMedidaIdSobra },
                              { salvarAgora: true }
                            )
                          }
                          podeAlterarUnd={podeAlterarUnd}
                          className={
                            podeEditarSobra
                              ? inputQtyTabelaClass
                              : `${inputQtyTabelaClass} cursor-not-allowed border-neutral-300 bg-neutral-100 text-neutral-800`
                          }
                          tabIndex={podeEditarSobra ? 0 : -1}
                        />
                      </td>
                        </>
                      ) : (
                        <>
                      {mostrarCamposBaseSobra ? (
                      <td className={celulaTabelaClass}>
                        <CampoQtyTabela
                          value={sobraExibicaoLinha(item, local, podeEditarSobra)}
                          onChange={(sobra) =>
                            atualizarLinha(linhaKey, { sobra })
                          }
                          disabled={!podeEditarSobra}
                          unds={unds}
                          undId={undSobraExibicaoLinha(item, local, podeEditarSobra)}
                          onUndChange={(undMedidaIdSobra) =>
                            atualizarLinha(
                              linhaKey,
                              { undMedidaIdSobra },
                              { salvarAgora: true }
                            )
                          }
                          podeAlterarUnd={podeAlterarUnd}
                          className={
                            podeEditarSobra
                              ? inputQtyTabelaClass
                              : `${inputQtyTabelaClass} cursor-not-allowed border-neutral-300 bg-neutral-100 text-neutral-800`
                          }
                          tabIndex={podeEditarSobra ? 0 : -1}
                        />
                      </td>
                      ) : null}
                      {mostraLancamentoProducao && (
                      <>
                      <td
                        className={celulaTabelaClass}
                        title={bloqueioLancamento ?? undefined}
                      >
                        <div className="min-w-0">
                          <CampoQtyTabela
                          value={foraDaUnidade ? "" : local?.producao || ""}
                          onChange={(producao) =>
                            atualizarLinha(linhaKey, { producao })
                          }
                          disabled={!podeEditarProducaoItem}
                          unds={unds}
                          undId={undFichaDoItem(
                            item,
                            local?.undMedidaIdProducao
                          )}
                          onUndChange={(undMedidaIdProducao) =>
                            atualizarLinha(
                              linhaKey,
                              { undMedidaIdProducao },
                              { salvarAgora: true }
                            )
                          }
                          podeAlterarUnd={podeAlterarUnd}
                          placeholder={
                            foraDaUnidade
                              ? "—"
                              : podeEditarProducao &&
                                  !(local?.producao ?? "").trim()
                                ? sugestaoProducaoLinha(item, local, unds) ||
                                  "—"
                                : undefined
                          }
                          className={classProducaoInput(
                            podeEditarProducaoItem,
                            local,
                            producaoTravadaPorAssinatura,
                            "table"
                          )}
                          tabIndex={podeEditarProducaoItem ? 0 : -1}
                        />
                          {bloqueioLancamento ? (
                            <p className="mt-1 text-[10px] font-medium leading-snug text-amber-700">
                              {bloqueioLancamento}
                            </p>
                          ) : null}
                        </div>
                      </td>
                      {mostraProduziu && (
                      <td className={`${celulaTabelaClass} text-center`}>
                        <div className="flex flex-col items-center gap-1">
                          <ProducaoToggle
                            checked={local?.producaoChecada === true}
                            estado={estadoProducao}
                            disabled={
                              !podeEditarProducao ||
                              foraDaUnidade ||
                              Boolean(bloqueioLancamento)
                            }
                            layout="tabela"
                            itemDescricao={item.descricao}
                            onChange={
                              podeEditarProducao && !bloqueioLancamento
                                ? (checada) =>
                                    void handleToggleProduzido(
                                      linhaKey,
                                      item.descricao,
                                      checada
                                    )
                                : undefined
                            }
                          />
                          {podeEditarProducao && !foraDaUnidade && !(local?.producaoChecada) && (
                            <button
                              type="button"
                              className={`mt-1 rounded px-2 py-0.5 text-[10px] font-semibold ${
                                local?.naoProduzidoPelaUnidade
                                  ? "bg-red-100 text-red-700 ring-1 ring-red-300"
                                  : "bg-neutral-100 text-neutral-600 hover:bg-red-50 hover:text-red-700"
                              }`}
                              onClick={() => void handleNaoProduziu(linhaKey, item.descricao)}
                            >
                              {local?.naoProduzidoPelaUnidade ? "Não produziu ✓" : "Não produziu"}
                            </button>
                          )}
                        </div>
                      </td>
                      )}
                      </>
                      )}
                        </>
                      )}
                      {mostraCronometro && (item.indexNoGrupo ?? 0) === 0 && (
                        <td
                          className={`${celulaTabelaClass} text-center align-middle`}
                          rowSpan={item.totalLinhasGrupo ?? 1}
                        >
                          {foraDaUnidade || ocultaCronometroPorSugestao ? (
                            <span className="text-xs font-medium text-slate-600">
                              —
                            </span>
                          ) : (
                            <div className="flex justify-center">
                              <CronometroItemButtons
                                cronometragem={cronometragemComTick(
                                  item.cronometragem,
                                  nowTick
                                )}
                                disabled={!podeEditarProducao}
                                somenteLeitura={cronometroSomenteLeitura}
                                onIniciar={() =>
                                  void acionarCronometroItem(
                                    item.itemId,
                                    "iniciar",
                                    item.descricao
                                  )
                                }
                                onPausar={() =>
                                  void acionarCronometroItem(
                                    item.itemId,
                                    "pausar",
                                    item.descricao
                                  )
                                }
                                onRetomar={() =>
                                  void acionarCronometroItem(
                                    item.itemId,
                                    "retomar",
                                    item.descricao
                                  )
                                }
                              />
                            </div>
                          )}
                        </td>
                      )}
                      {layoutFichaUnificada ? (
                        <>
                          {mostraLancamentoProducao ? (
                          <td className={celulaTabelaClass}>
                            <button
                              type="button"
                              onClick={() =>
                                abrirModalObservacao(
                                  linhaKey,
                                  item.descricao,
                                  "producao"
                                )
                              }
                              title={
                                local?.observacaoProducao?.trim() ||
                                "Obs. produção"
                              }
                              aria-label="Observação da produção"
                              className={classBotaoObservacaoProducao(
                                local,
                                podeEditarProducao,
                                "tabela"
                              )}
                            >
                              <span className="truncate">Obs. produção</span>
                              {Boolean(local?.observacaoProducao?.trim()) && (
                                <IndicadorObsPreenchida />
                              )}
                            </button>
                          </td>
                          ) : null}
                          <td className={celulaTabelaClass}>
                            <button
                              type="button"
                              onClick={() =>
                                abrirModalObservacao(
                                  linhaKey,
                                  item.descricao,
                                  "sobra"
                                )
                              }
                              title={
                                local?.observacaoSobra?.trim() || "Obs. sobra"
                              }
                              aria-label="Observação da sobra"
                              className={classBotaoObservacaoSobra(
                                local,
                                podeEditarSobra,
                                "tabela"
                              )}
                            >
                              <span className="truncate">Obs. sobra</span>
                              {Boolean(local?.observacaoSobra?.trim()) && (
                                <IndicadorObsPreenchida />
                              )}
                            </button>
                          </td>
                        </>
                      ) : (
                      <td className={celulaTabelaClass}>
                        <div className="flex flex-col gap-1">
                          {mostrarCamposBaseSobra ? (
                          <button
                            type="button"
                            onClick={() =>
                              abrirModalObservacao(
                                linhaKey,
                                item.descricao,
                                "sobra"
                              )
                            }
                            title={
                              local?.observacaoSobra?.trim() || "Obs. sobra"
                            }
                            aria-label="Observação da sobra"
                            className={classBotaoObservacaoSobra(
                              local,
                              podeEditarSobra,
                              "tabela"
                            )}
                          >
                            <span className="truncate">Obs. sobra</span>
                            {Boolean(local?.observacaoSobra?.trim()) && (
                              <IndicadorObsPreenchida />
                            )}
                          </button>
                          ) : null}
                          {mostraLancamentoProducao && (
                          <button
                            type="button"
                            onClick={() =>
                              abrirModalObservacao(
                                linhaKey,
                                item.descricao,
                                "producao",
                                !local?.producaoChecada &&
                                  !foraDaUnidade &&
                                  podeEditarProducao
                                  ? { justificativaNaoProduzido: true }
                                  : undefined
                              )
                            }
                            title={
                              local?.observacaoProducao?.trim() ||
                              "Obs. produção"
                            }
                            aria-label="Observação da produção"
                            className={classBotaoObservacaoProducao(
                              local,
                              podeEditarProducao,
                              "tabela"
                            )}
                          >
                            <span className="truncate">Obs. produção</span>
                            {Boolean(local?.observacaoProducao?.trim()) && (
                              <IndicadorObsPreenchida />
                            )}
                          </button>
                          )}
                        </div>
                      </td>
                      )}
                    </tr>
                  );
                })}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>

          <div className="print-only">
            <FichaDiariaPrintSheet
              itens={payload?.itens || []}
              linhas={linhas}
              unds={unds}
              diaSemanaLabel={diaSemanaImpressao}
              dataFormatada={dataBr}
              nomeLoja={nomeLojaSelecionada}
              nomeSetor={agruparPorSetor ? null : setorAtual?.nome}
              mostrarCronometro={mostraCronometro}
              dataFicha={dataFicha}
              modoNoite={layoutFichaUnificada}
              agruparPorSetor={agruparPorSetor}
              setoresOrdem={payload?.resumoSetores}
              assinaturasSetores={payload?.assinaturasSetores}
              ficha={ficha}
              orientacao={payload?.orientacao}
            />
          </div>

          <div className="no-print mt-4 hidden items-center justify-end gap-2 xl:flex">
            <button
              type="button"
              onClick={() => void saveFicha({ silent: false })}
              disabled={!podeSalvar || savingManual}
              className="rounded-md border border-neutral-300 px-4 py-2 text-sm font-semibold text-neutral-700 disabled:opacity-60"
            >
              {savingManual ? "Salvando..." : "Salvar agora"}
            </button>
            {payload?.podeImprimir && (
              <button
                type="button"
                onClick={imprimirFicha}
                className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white"
              >
                Imprimir Ficha
              </button>
            )}
            {mostraPausas && (
              <button
                type="button"
                onClick={handleVerPausas}
                className="rounded-md bg-amber-600 px-4 py-2 text-sm font-semibold text-white"
              >
                Ver Pausas
              </button>
            )}
          </div>

          <div
            className={`no-print fixed inset-x-0 bottom-0 z-30 border-t border-neutral-200 bg-white/95 px-3 pt-2 shadow-[0_-6px_16px_rgba(0,0,0,0.08)] backdrop-blur xl:hidden ${
              tecladoAberto ? "hidden" : ""
            }`}
            style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
          >
            <div className="mx-auto flex max-w-7xl gap-2">
              <button
                type="button"
                onClick={() => void saveFicha({ silent: false })}
                disabled={!podeSalvar || savingManual}
                className="min-h-12 flex-1 rounded-lg border border-neutral-300 px-4 text-sm font-semibold text-neutral-800 touch-manipulation disabled:opacity-60"
              >
                {savingManual ? "Salvando..." : autoSaving ? "Salvo" : "Salvar"}
              </button>
              {payload?.podeImprimir && (
                <button
                  type="button"
                  onClick={imprimirFicha}
                  className="min-h-12 flex-1 rounded-lg bg-primary px-4 text-sm font-semibold text-white touch-manipulation"
                >
                  Imprimir
                </button>
              )}
              {mostraPausas && (
                <button
                  type="button"
                  onClick={handleVerPausas}
                  className="min-h-12 flex-1 rounded-lg bg-amber-600 px-4 text-sm font-semibold text-white touch-manipulation"
                >
                  Pausas
                </button>
              )}
            </div>
          </div>

          <div className="no-print">
            <OrientacoesProducaoDisplay orientacao={payload?.orientacao} />
          </div>
        </>
      )}

      {!loading && payload ? (
        <div className="no-print">
          <FichaDiariaAssinaturas
            ficha={ficha}
            setorNome={setorAtual?.nome}
            assinaturaSetor={assinaturaSetorAtual}
            turnoDia={turnoEhDia}
            podeAssinarConferencia={podeAssinarConferencia}
            podeAssinarProducao={podeAssinarProducao}
            podeAssinarProducaoTotal={podeAssinarProducaoTotal}
            prontaParaAssinarConferencia={prontaParaAssinarConferencia}
            fichaProntaParaAssinarProducao={fichaProntaParaAssinarProducao}
            motivosConferencia={motivosAssinaturaConferencia}
            motivosProducao={motivosAssinaturaProducao}
            assinandoConferencia={assinandoConferencia}
            assinandoProducao={assinandoProducao}
            assinandoProducaoTotal={assinandoProducaoTotal}
            onAssinarConferencia={abrirModalAssinaturaConferencia}
            onAssinarProducao={() => setModalAssinatura("producao")}
            onAssinarProducaoTotal={() => setModalAssinatura("producao_total")}
            pendenciasProducaoTotal={payload.pendenciasProducaoTotal}
            mostrarPdfConferencia={
              (layoutFichaUnificada || turnoEhNoite) &&
              (payload.itens?.length ?? 0) > 0
            }
            preparandoPdfConferencia={preparandoSobrasPdf}
            onVerPdfConferencia={() => void verPdfSobrasConferencia()}
            onWhatsAppConferencia={() => void encaminharSobrasWhatsApp()}
          />
        </div>
      ) : null}

      {!loading &&
        !exigeSetor &&
        baseConfirmada &&
        (payload?.itens?.length ?? 0) === 0 && (
        <p className="mt-4 text-sm text-neutral-500">
          Nenhum item ativo para preencher a ficha nesta loja.
        </p>
      )}

      <FichaDiariaVisaoCompletaModal
        isOpen={modalFichaCompleta}
        onClose={() => setModalFichaCompleta(false)}
        dataFicha={dataFicha}
        lojaId={selectedLojaId}
        turnoSimulado={turnoSimuladoEnvio}
        onAbrirEquipeSetor={setEquipeSetor}
      />
      <FichaDiariaEquipeSetorModal
        setor={equipeSetor}
        lojaId={selectedLojaId}
        onClose={() => setEquipeSetor(null)}
      />
      <Modal
        isOpen={modalObservacao !== null}
        onClose={() => setModalObservacao(null)}
        bodyClassName="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4 sm:p-6"
        title={
          modalObservacao?.justificativaNaoProduzido
            ? "Justificativa — não produzido"
            : modalObservacao?.tipo === "sobra"
              ? "Observação da sobra"
              : "Observação da produção"
        }
      >
        {modalObservacao && (
          <div className="space-y-4">
            <p className="text-sm font-medium leading-relaxed text-neutral-800">
              {modalObservacao.descricao}
            </p>
            {modalObservacao.justificativaNaoProduzido && (
              <p className="text-sm leading-relaxed text-amber-900">
                Informe o motivo de não ter produzido este item. O texto será
                salvo na observação de produção.
              </p>
            )}
            {(() => {
              const podeEditarModal =
                modalObservacao.tipo === "sobra"
                  ? podeEditarSobra
                  : podeEditarProducao;
              return (
                <>
                  <textarea
                    value={observacaoDraft}
                    onChange={(e) => setObservacaoDraft(e.target.value)}
                    readOnly={!podeEditarModal}
                    rows={linhasTextareaObservacao}
                    placeholder={
                      modalObservacao.justificativaNaoProduzido
                        ? "Ex.: falta de insumo, equipamento quebrado, item cancelado…"
                        : modalObservacao.tipo === "sobra"
                          ? "Observação sobre a sobra (ex: qualidade, motivo…)"
                          : "Observação sobre a produção (ex: ok, falta embalagem…)"
                    }
                    className={`max-h-[min(50vh,24rem)] min-h-[9rem] w-full resize-y overflow-y-auto whitespace-pre-wrap rounded-lg border border-neutral-300 px-4 py-3 text-base leading-relaxed lg:text-sm ${
                      !podeEditarModal
                        ? "cursor-default bg-neutral-100 text-neutral-800"
                        : "bg-white text-neutral-900"
                    }`}
                    autoFocus={podeEditarModal}
                  />
                  <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                    <button
                      type="button"
                      onClick={() => setModalObservacao(null)}
                      className="min-h-12 rounded-lg border border-neutral-300 px-4 text-sm font-semibold text-neutral-700 touch-manipulation sm:min-h-0 sm:py-2"
                    >
                      {podeEditarModal ? "Cancelar" : "Fechar"}
                    </button>
                    {podeEditarModal && (
                      <button
                        type="button"
                        onClick={salvarObservacaoModal}
                        className="min-h-12 rounded-lg bg-primary px-4 text-sm font-semibold text-white touch-manipulation sm:min-h-0 sm:py-2"
                      >
                        Salvar observação
                      </button>
                    )}
                  </div>
                </>
              );
            })()}
          </div>
        )}
      </Modal>

      <Modal
        isOpen={modalAssinatura !== null}
        onClose={() => !assinaturaEmAndamento && setModalAssinatura(null)}
        bodyClassName="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4 sm:p-6"
        title={
          modalAssinatura === "conferencia"
            ? "Assinar conferência"
            : modalAssinatura === "producao_total"
              ? "Assinar produção total dos setores"
              : "Assinar produção do setor"
        }
      >
        {assinaturaEmAndamento ? (
          <div className="mt-6 flex flex-col items-center gap-3 py-2">
            <AssinaturaPenAnimacao className="max-w-[200px]" />
            <p className="text-sm font-medium text-neutral-600">
              Registrando assinatura…
            </p>
          </div>
        ) : (
          <>
            <p className="text-sm text-neutral-700">
              {modalAssinatura === "conferencia"
                  ? "Deseja assinar a conferência desta ficha?"
                  : modalAssinatura === "producao_total"
                    ? "Deseja assinar a produção total dos setores? Você poderá confirmar mesmo com setores ou itens pendentes."
                    : setorAtual?.nome
                      ? `Deseja assinar a produção do ${setorAtual.nome} desta ficha?`
                      : "Deseja assinar a produção desta ficha?"}
            </p>
            {modalAssinatura === "producao_total" &&
            ((payload?.pendenciasProducaoTotal?.setoresSemAssinatura.length ??
              0) > 0 ||
              (payload?.pendenciasProducaoTotal?.itensPendentes.length ?? 0) >
                0) ? (
              <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950">
                <p className="font-semibold">Há pendências. Você pode assinar mesmo assim.</p>
                <ul className="mt-2 list-disc space-y-1 pl-5 text-xs">
                  {(payload?.pendenciasProducaoTotal?.setoresSemAssinatura ?? []).map(
                    (s) => (
                      <li key={`setor-${s.setorId}`}>
                        Setor {s.setorNome} ainda não assinou.
                      </li>
                    )
                  )}
                  {(payload?.pendenciasProducaoTotal?.itensPendentes ?? [])
                    .slice(0, 12)
                    .map((i) => (
                      <li key={`${i.setorNome}-${i.descricao}`}>
                        {i.setorNome}: {i.descricao} sem produção/justificativa.
                      </li>
                    ))}
                </ul>
              </div>
            ) : null}
            {modalAssinatura === "conferencia" && nenhumItemComSobra && (
              <label className="mt-4 block text-sm text-neutral-700">
                Justificativa (sem sobra)
                <textarea
                  value={justificativaSemSobraDraft}
                  onChange={(e) => setJustificativaSemSobraDraft(e.target.value)}
                  rows={3}
                  maxLength={300}
                  className="mt-1 min-h-11 w-full rounded-lg border border-neutral-300 px-3 py-2 text-base lg:text-sm"
                  placeholder={JUSTIFICATIVA_SEM_SOBRA_PADRAO}
                />
              </label>
            )}
            <p className="mt-2 text-sm text-neutral-600">
              Após assinar, este campo não poderá mais ser alterado.
            </p>
            <div className="mt-4">
              <SignaturePadField
                key={modalAssinatura ?? "fechado"}
                label="Assine abaixo para confirmar"
                disabled={assinaturaEmAndamento}
                onSignedChange={(assinado, imagem) => {
                  setAssinaturaDesenhada(assinado);
                  setAssinaturaImagem(imagem ?? null);
                }}
              />
              <p className="mt-1.5 text-xs text-neutral-500">
                {assinaturaDesenhada
                  ? "Assinatura capturada."
                  : "Desenhe sua assinatura no campo acima."}
              </p>
            </div>
            <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                disabled={assinaturaEmAndamento}
                onClick={() => setModalAssinatura(null)}
                className="min-h-12 rounded-lg border border-neutral-300 px-4 text-sm font-semibold text-neutral-700 touch-manipulation disabled:opacity-60 sm:min-h-0 sm:py-2"
              >
                Cancelar
              </button>
              <button
                type="button"
                disabled={assinaturaEmAndamento || !assinaturaDesenhada}
                onClick={() =>
                  void (modalAssinatura === "conferencia"
                    ? executarAssinaturaConferencia()
                    : modalAssinatura === "producao_total"
                      ? executarAssinaturaProducaoTotal()
                      : executarAssinaturaProducao())
                }
                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-lg bg-primary px-4 text-sm font-semibold text-white touch-manipulation disabled:opacity-60 sm:min-h-0 sm:py-2"
              >
                <PenLine className="h-4 w-4" />
                Confirmar assinatura
              </button>
            </div>
          </>
        )}
      </Modal>

      {pausasModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="max-h-[80vh] w-full max-w-xl overflow-y-auto rounded-xl bg-white p-5 shadow-xl">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-bold text-neutral-800">Pausas do dia</h2>
              <button
                type="button"
                onClick={() => setPausasModalOpen(false)}
                className="text-neutral-500 hover:text-neutral-800"
              >
                ✕
              </button>
            </div>
            {loadingPausas ? (
              <p className="text-center text-neutral-500">Carregando...</p>
            ) : pausas.length === 0 ? (
              <p className="text-center text-neutral-500">Nenhuma pausa registrada.</p>
            ) : (() => {
              const usuarios = Array.from(new Set(pausas.map((p) => p.usuarioNome)));
              const itensUnicos = Array.from(new Set(pausas.map((p) => p.itemDescricao)));
              const LIMIAR_LONGA_MS = 5 * 60 * 1000;
              const POR_PAGINA = 5;

              const filtered = pausas.filter((p) => {
                if (pausaFiltroUsuario && p.usuarioNome !== pausaFiltroUsuario) return false;
                if (pausaFiltroItem && p.itemDescricao !== pausaFiltroItem) return false;
                if (pausaFiltroLongas && p.retomadoEm) {
                  const diff = new Date(p.retomadoEm).getTime() - new Date(p.pausadoEm).getTime();
                  if (diff < LIMIAR_LONGA_MS) return false;
                }
                if (pausaFiltroLongas && !p.retomadoEm) return true;
                return true;
              });

              const grouped = filtered.reduce<Record<string, PausaFichaItem[]>>((acc, p) => {
                const key = p.itemDescricao;
                if (!acc[key]) acc[key] = [];
                acc[key].push(p);
                return acc;
              }, {});

              const entries = Object.entries(grouped);
              const totalPaginas = Math.ceil(entries.length / POR_PAGINA);
              const paginaAtual = Math.min(pausaPagina, totalPaginas - 1);
              const entriesPaginados = entries.slice(paginaAtual * POR_PAGINA, (paginaAtual + 1) * POR_PAGINA);

              return (
                <>
                  <div className="mb-4 rounded-lg bg-neutral-50 p-3">
                    <div className="grid grid-cols-3 items-end gap-2">
                      <div className="min-w-0">
                        <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-neutral-500">Usuário</label>
                        <select
                          value={pausaFiltroUsuario}
                          onChange={(e) => { setPausaFiltroUsuario(e.target.value); setPausaPagina(0); }}
                          className="h-9 w-full rounded-md border border-neutral-200 bg-white px-2 py-2 text-xs shadow-sm focus:border-amber-400 focus:outline-none focus:ring-1 focus:ring-amber-400"
                        >
                          <option value="">Todos</option>
                          {usuarios.map((u) => <option key={u} value={u}>{u}</option>)}
                        </select>
                      </div>
                      <div className="min-w-0">
                        <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-neutral-500">Item</label>
                        <select
                          value={pausaFiltroItem}
                          onChange={(e) => { setPausaFiltroItem(e.target.value); setPausaPagina(0); }}
                          className="h-9 w-full rounded-md border border-neutral-200 bg-white px-2 py-2 text-xs shadow-sm focus:border-amber-400 focus:outline-none focus:ring-1 focus:ring-amber-400"
                        >
                          <option value="">Todos</option>
                          {itensUnicos.map((i) => <option key={i} value={i}>{i}</option>)}
                        </select>
                      </div>
                      <div className="min-w-0">
                        <label className="flex h-9 w-full cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-neutral-200 bg-white px-2 text-xs shadow-sm transition hover:border-amber-300">
                          <input
                            type="checkbox"
                            checked={pausaFiltroLongas}
                            onChange={(e) => { setPausaFiltroLongas(e.target.checked); setPausaPagina(0); }}
                            className="h-4 w-4 shrink-0 rounded border-neutral-300 text-amber-600 focus:ring-amber-500"
                          />
                          <span className="whitespace-nowrap font-medium text-neutral-700">Só longas (5min+)</span>
                        </label>
                      </div>
                    </div>
                    <p className="mt-2 text-[11px] text-neutral-400">{filtered.length} pausa{filtered.length !== 1 ? "s" : ""} encontrada{filtered.length !== 1 ? "s" : ""}</p>
                  </div>
                  {filtered.length === 0 ? (
                    <p className="text-center text-sm text-neutral-400">Nenhuma pausa com esses filtros.</p>
                  ) : (
                    <>
                      <div className="space-y-2">
                        {entriesPaginados.map(([itemDesc, items]) => (
                          <details key={itemDesc} open={entriesPaginados.length === 1} className="group rounded-lg border border-neutral-200">
                            <summary className="flex cursor-pointer items-center justify-between px-3 py-2.5 font-semibold text-neutral-800">
                              <span>{itemDesc}</span>
                              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">{items.length} {items.length === 1 ? "pausa" : "pausas"}</span>
                            </summary>
                            <div className="space-y-2 border-t border-neutral-100 px-3 pb-3 pt-2">
                              {items.map((p, idx) => (
                                <div key={idx} className="rounded-md bg-neutral-50 p-2.5">
                                  <div className="flex items-center justify-between text-xs text-neutral-500">
                                    <span>{p.usuarioNome}</span>
                                    {p.retomadoEm && (() => {
                                      const diffMs = new Date(p.retomadoEm).getTime() - new Date(p.pausadoEm).getTime();
                                      const totalSec = Math.floor(diffMs / 1000);
                                      const min = Math.floor(totalSec / 60);
                                      const sec = totalSec % 60;
                                      const label = min > 0 ? `${min}min ${sec}s` : `${sec}s`;
                                      return <span className="font-semibold text-amber-700">{label}</span>;
                                    })()}
                                  </div>
                                  <div className="mt-1 flex gap-4 text-xs text-neutral-600">
                                    <span>Pausado: {new Date(p.pausadoEm).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</span>
                                    {p.retomadoEm && (
                                      <span>Retomado: {new Date(p.retomadoEm).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</span>
                                    )}
                                  </div>
                                  {p.justificativa ? (
                                    <p className="mt-1 text-sm text-neutral-700">
                                      <span className="font-medium">Motivo:</span> {p.justificativa}
                                    </p>
                                  ) : (
                                    <p className="mt-1 text-xs italic text-neutral-400">Sem justificativa</p>
                                  )}
                                </div>
                              ))}
                            </div>
                          </details>
                        ))}
                      </div>
                      {totalPaginas > 1 && (
                        <div className="mt-3 flex items-center justify-center gap-1">
                          <button
                            type="button"
                            disabled={paginaAtual === 0}
                            onClick={() => setPausaPagina((p) => p - 1)}
                            className="rounded-md px-2.5 py-1.5 text-xs font-medium text-neutral-600 transition hover:bg-neutral-100 disabled:opacity-40"
                          >
                            ← Anterior
                          </button>
                          <span className="px-3 text-xs text-neutral-500">{paginaAtual + 1} / {totalPaginas}</span>
                          <button
                            type="button"
                            disabled={paginaAtual >= totalPaginas - 1}
                            onClick={() => setPausaPagina((p) => p + 1)}
                            className="rounded-md px-2.5 py-1.5 text-xs font-medium text-neutral-600 transition hover:bg-neutral-100 disabled:opacity-40"
                          >
                            Próxima →
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      )}

    </div>
  );
};

export default ControleProducaoFichaDiariaPage;
export { ControleProducaoFichaDiariaPage };
